import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRedirectResult, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  FunnelIcon,
  MinusIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  PlusIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { auth, googleProvider, appleProvider } from './config/firebase';
import { clearToken, getToken, referralUrl, salesApi, saveToken } from './services/api';

const tabs = [
  { id: 'today', label: 'Today', icon: SparklesIcon },
  { id: 'leads', label: 'Leads', icon: FunnelIcon },
  { id: 'commissions', label: 'Commissions', icon: BanknotesIcon },
  { id: 'ask', label: 'Ask Flock', icon: ChatBubbleLeftRightIcon },
  { id: 'impact', label: 'Impact', icon: UserGroupIcon },
  { id: 'admin', label: 'Admin', icon: BellIcon },
];

const statusActions = [
  { status: 'contacted', label: 'Contacted', icon: CheckCircleIcon },
  { status: 'nurture', label: 'Nurture', icon: PhoneIcon },
  { status: 'dead', label: 'Dead', icon: NoSymbolIcon },
  { status: 'handoff', label: 'Handoff', icon: ArrowRightOnRectangleIcon },
];

const queueLabels = {
  founder: 'Popular',
  warm: 'Medium',
  standard: 'Low',
};

const emptyWorkbench = {
  available: [],
  mine: [],
  counts: {
    available: { all: 0, founder: 0, warm: 0, standard: 0 },
    mine: { all: 0, new: 0, contacted: 0, nurture: 0 },
  },
};

function consumeDevTokenFromUrl() {
  if (!import.meta.env.DEV) return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get('devToken');
  if (!token) return null;
  saveToken(token);
  url.searchParams.delete('devToken');
  window.history.replaceState({}, '', url.toString());
  return token;
}

function leadIdFromPath(pathname) {
  const match = pathname.split('?')[0].match(/^\/leads\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function dealRouteFromPath(pathname) {
  const [path, search = ''] = pathname.split('?');
  const match = path.match(/^\/deals\/([^/]+)$/);
  if (!match) return null;
  const params = new URLSearchParams(search);
  return {
    organizationId: decodeURIComponent(match[1]),
    repUserId: params.get('repUserId') || null,
  };
}

function payoutRouteFromPath(pathname) {
  const [path, search = ''] = pathname.split('?');
  if (path === '/payouts/pending') {
    const params = new URLSearchParams(search);
    return { type: 'pending', repUserId: params.get('repUserId') || null };
  }
  const match = path.match(/^\/payouts\/([^/]+)$/);
  if (!match) return null;
  return { type: 'paid', payoutId: decodeURIComponent(match[1]) };
}

function useRoutePath() {
  const [path, setPath] = useState(() => `${window.location.pathname}${window.location.search}`);

  useEffect(() => {
    const handlePopState = () => setPath(`${window.location.pathname}${window.location.search}`);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((nextPath) => {
    // fromApp marks entries pushed by in-app navigation, so back buttons can
    // use real history (returning to wherever the user actually came from)
    // and fall back to a static target only on deep links.
    window.history.pushState({ fromApp: true }, '', nextPath);
    setPath(`${window.location.pathname}${window.location.search}`);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return [path, navigate];
}

function formatMoney(cents = 0) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(cents || 0) / 100);
}

function formatDate(value) {
  if (!value) return 'Open';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value));
}

function formatLongDate(value) {
  if (!value) return 'Open';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
}

function formatRule(rule) {
  if (!rule) return 'No rule';
  if (Number(rule.percentage_bps || 0) > 0) return `${Number(rule.percentage_bps) / 100}%`;
  if (Number(rule.fixed_amount_cents || 0) > 0) return `${formatMoney(rule.fixed_amount_cents)} flat`;
  return rule.name || 'Commission rule';
}

function termLabel(term) {
  if (!term || term.ongoing) return 'Ongoing';
  if (term.expired) return `Ended ${formatLongDate(term.termEnd)}`;
  return `Through ${formatLongDate(term.termEnd)}`;
}

function periodLabel(value) {
  if (!value) return 'Any period';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function tierLabel(value) {
  if (!value) return 'Any tier';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function statusLabel(value) {
  if (!value) return 'Open';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function groupCommissionDeals(commissions) {
  const deals = new Map();
  commissions.forEach((item) => {
    // Payout offsets are rep-level bookkeeping, not deals — they live in the
    // payout history + statements, not the deals table.
    if (item.event_type === 'payout') return;
    const pendingAttribution = item.event_type === 'referral_pending_org' || item.metadata?.pendingOrganization;
    const pendingPayment = item.event_type === 'referral_pending_payment' || item.metadata?.pendingPayment;
    const preCommission = pendingAttribution || pendingPayment;
    const key = `${item.rep_user_id || 'rep'}:${item.organization_id || item.metadata?.attributionId || item.id}`;
    const existing = deals.get(key) || {
      key,
      repUserId: item.rep_user_id,
      organizationId: item.organization_id,
      attributionId: item.metadata?.attributionId || null,
      organizationName: item.organization_name || 'Manual commission',
      repName: item.rep_display_name || item.rep_name || item.rep_email || 'Sales rep',
      tierName: pendingAttribution ? 'Email attached - waiting for org' : item.tier_name || item.tier_code || item.metadata?.tierCode || 'Tier open',
      pendingAttribution,
      pendingPayment,
      eventCount: 0,
      pendingCents: 0,
      paidCents: 0,
      lifetimeCents: 0,
      monthsConsumed: 0,
      termMonths: null,
      paidAt: null,
      latestAt: item.event_timestamp || item.created_at,
      latestStatus: item.settlement_status || item.status,
      latestEvent: item.event_type,
    };

    const amount = Number(item.amount_cents || 0);
    // Ledger rows are immutable ('pending' forever); settlement_status is the
    // payout-aware answer to "has this been paid out?".
    const displayStatus = item.settlement_status || item.status;
    if (!preCommission) existing.eventCount += 1;
    existing.pendingAttribution = existing.pendingAttribution || pendingAttribution;
    existing.pendingPayment = existing.pendingPayment || pendingPayment;
    existing.lifetimeCents += amount;
    if (displayStatus === 'pending') existing.pendingCents += amount;
    if (displayStatus === 'paid') existing.paidCents += amount;

    // Term progress: each accrual consumed commission-months (monthly = 1,
    // annual = 12; prorations record the fraction in metadata).
    if (item.event_type === 'accrual') {
      const consumed = Number(item.metadata?.commissionMonths);
      existing.monthsConsumed += Number.isFinite(consumed) && consumed > 0
        ? consumed
        : (item.metadata?.billingPeriod === 'annual' ? 12 : 1);
      if (item.rule_duration_months != null) existing.termMonths = Number(item.rule_duration_months);
    }
    if (item.settled_at && (!existing.paidAt || new Date(item.settled_at) > new Date(existing.paidAt))) {
      existing.paidAt = item.settled_at;
    }

    const eventAt = item.event_timestamp || item.created_at;
    if (!existing.latestAt || new Date(eventAt) > new Date(existing.latestAt)) {
      existing.latestAt = eventAt;
      existing.latestStatus = item.settlement_status || item.status;
      existing.latestEvent = item.event_type;
    }

    deals.set(key, existing);
  });

  return Array.from(deals.values()).sort((a, b) => new Date(b.latestAt || 0) - new Date(a.latestAt || 0));
}

// "Last seen" = the church's most recent demand signal (live from the cluster;
// falls back to the lead's own creation for manual leads). Recent activity gets
// a pill so fresh demand pops out of the list — lead creation dates are useless
// for this (the initial import creates everything at once).
const LEAD_FRESH_DAYS = 7;
function leadLastSeenLabel(lead) {
  const value = lead.demand_last_seen_at || lead.created_at;
  if (!value) return <span className="text-sm font-semibold text-[#5f6b66]">-</span>;
  const fresh = Date.now() - new Date(value).getTime() < LEAD_FRESH_DAYS * 24 * 60 * 60 * 1000;
  if (fresh) {
    return <span className="status-pill">{formatDate(value)}</span>;
  }
  return <span className="text-sm font-semibold text-[#5f6b66]">{formatDate(value)}</span>;
}

// Leads table columns with the value each one sorts by. Queue sorts by its
// display label so the order matches what's on screen; date columns sort by
// timestamp with "Last seen" using the same live-cluster-or-created fallback
// the cell renders.
const leadSortColumns = [
  { key: 'church', label: 'Church', value: (lead) => (lead.canonical_name || '').toLowerCase() },
  { key: 'queue', label: 'Queue', value: (lead) => queueLabels[lead.queue_type] || lead.queue_type || '' },
  { key: 'signals', label: 'Signals', value: (lead) => Number(lead.signal_count || 0) },
  { key: 'status', label: 'Status', value: (lead) => lead.status || '' },
  {
    key: 'lastSeen',
    label: 'Last seen',
    value: (lead) => {
      const raw = lead.demand_last_seen_at || lead.created_at;
      return raw ? new Date(raw).getTime() : null;
    },
  },
  { key: 'next', label: 'Next', value: (lead) => (lead.next_action_at ? new Date(lead.next_action_at).getTime() : null) },
];

function sortLeads(leads, sort) {
  const column = sort && leadSortColumns.find((col) => col.key === sort.key);
  if (!column) return leads;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...leads].sort((a, b) => {
    const aVal = column.value(a);
    const bVal = column.value(b);
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1; // empty cells stay at the bottom either way
    if (bVal == null) return -1;
    if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
    return (aVal - bVal) * dir;
  });
}

function formatCommissionMonths(value) {
  const n = Number(value || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// "3 of 12" payments received toward the deal's term; "Term complete" when the
// term is fully earned (an annual deal completes after its single payment);
// "Ongoing" for rules with no term.
function dealProgressLabel(deal) {
  if (deal.pendingAttribution || deal.pendingPayment) {
    return <span className="text-sm font-semibold text-[#5f6b66]">-</span>;
  }
  if (deal.termMonths == null) {
    return <span className="text-sm font-semibold text-[#5f6b66]">Ongoing</span>;
  }
  if (deal.monthsConsumed >= deal.termMonths) {
    return <span className="status-pill text-emerald-700">Term complete</span>;
  }
  return (
    <span className="text-sm font-bold">
      {formatCommissionMonths(deal.monthsConsumed)} of {formatCommissionMonths(deal.termMonths)}
    </span>
  );
}

function chartEventDate(item) {
  return item.event_timestamp || item.projectedDate || item.created_at;
}

function buildCommissionChartPoints({ ledger = [], schedule = [], paymentLimit = 12 }) {
  const accruedPayments = ledger
    .filter((item) => item.event_type === 'accrual' && Number(item.amount_cents || 0) > 0)
    .sort((a, b) => new Date(chartEventDate(a) || 0) - new Date(chartEventDate(b) || 0))
    .map((item) => ({
      kind: 'accrued',
      amountCents: Number(item.amount_cents || 0),
      date: chartEventDate(item),
    }));

  const projectedPayments = schedule.map((item) => ({
    kind: 'projected',
    amountCents: Number(item.amountCents || 0),
    date: item.projectedDate,
  }));

  let cumulativeCents = 0;
  return [...accruedPayments, ...projectedPayments]
    .slice(0, paymentLimit)
    .map((item, index) => {
      cumulativeCents += item.amountCents;
      return {
        ...item,
        paymentNumber: index + 1,
        cumulativeCents,
      };
    });
}

function pointsToPolyline(points) {
  return points.map((point) => `${point.x},${point.y}`).join(' ');
}

function formatSourceType(value) {
  const labels = {
    fyc_search: 'Find Your Church search',
    lead_submission: 'Lead form submission',
    notify_me: 'Notify me request',
  };
  return labels[value] || 'App activity';
}

function firstNameFromDisplayName(value) {
  if (!value) return 'This seeker';
  return value.split(/\s+/)[0] || value;
}

function formatSignalDate(value) {
  return value ? formatDate(value) : 'Open';
}

function signalTotalLabel(summary) {
  const searches = Number(summary?.totalSearches || 0);
  if (searches > 0) return `${searches} ${searches === 1 ? 'search' : 'searches'}`;
  const signals = Number(summary?.totalSignals || 0);
  return `${signals} ${signals === 1 ? 'signal' : 'signals'}`;
}

function anonymousSignalText(group) {
  const count = Number(group?.anonymousCount || 0);
  if (!count) return '';
  const noun = group.sourceType === 'fyc_search'
    ? (count === 1 ? 'anonymous search' : 'anonymous searches')
    : (count === 1 ? 'anonymous signal' : 'anonymous signals');
  return `${count} ${noun} for "${group.label}". No member name or email was captured.`;
}

function seekerCountLabel(group) {
  const count = Number(group?.namedCount || 0);
  return `${count} ${count === 1 ? 'seeker' : 'seekers'}`;
}

function seekerPillClass(group) {
  return Number(group?.namedCount || 0) > 0
    ? 'status-pill seeker-pill'
    : 'status-pill seeker-pill muted';
}

function statusClass(status) {
  if (status === 'founder' || status === 'handoff') return 'hot';
  if (status === 'warm' || status === 'nurture') return 'warn';
  return '';
}

function statusActionClass(currentStatus, actionStatus) {
  return `ghost-button status-action-button ${currentStatus === actionStatus ? 'active' : ''}`;
}

function useSalesPortal() {
  const [session, setSession] = useState(() => {
    const token = consumeDevTokenFromUrl() || getToken();
    return token ? { token } : null;
  });
  const [activeTab, setActiveTab] = useState('today');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    me: null,
    summary: null,
    leads: [],
    workbench: emptyWorkbench,
    commissions: [],
    payouts: [],
    notifications: [],
    impact: null,
    reps: [],
    repCommissions: [],
    conflicts: [],
    rules: [],
  });

  const isAdmin = ['sales_owner', 'sales_lead'].includes(data.me?.profile?.role);

  const load = useCallback(async () => {
    if (!getToken()) return;
    setLoading(true);
    setError('');
    try {
      const me = await salesApi.me();
      const adminRole = ['sales_owner', 'sales_lead'].includes(me.profile?.role);
      const [
        summary,
        leads,
        workbench,
        commissions,
        payouts,
        notifications,
        impact,
      ] = await Promise.all([
        salesApi.summary(),
        adminRole ? salesApi.leads({ limit: 100 }) : Promise.resolve({ leads: [] }),
        adminRole ? Promise.resolve(emptyWorkbench) : salesApi.leadWorkbench(),
        salesApi.commissions(),
        salesApi.payouts(),
        salesApi.notifications(),
        salesApi.impact(),
      ]);

      const adminData = adminRole
        ? await Promise.all([
          salesApi.reps(),
          salesApi.repCommissions(),
          salesApi.conflicts(),
          salesApi.commissionRules(),
        ])
        : [{ reps: [] }, { reps: [] }, { conflicts: [] }, { rules: [] }];

      setData({
        me,
        summary,
        leads: adminRole ? leads.leads || [] : [...(workbench.mine || []), ...(workbench.available || [])],
        workbench: workbench || emptyWorkbench,
        commissions: commissions.ledger || [],
        payouts: payouts.payouts || [],
        notifications: notifications.notifications || [],
        impact: impact.impact || null,
        reps: adminData[0].reps || [],
        repCommissions: adminData[1].reps || [],
        conflicts: adminData[2].conflicts || [],
        rules: adminData[3].rules || [],
      });
    } catch (err) {
      setError(err.body?.message || err.message || 'Sales portal request failed');
      if (err.status === 401) {
        clearToken();
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, session]);

  return {
    activeTab,
    setActiveTab,
    loading,
    error,
    setError,
    data,
    setData,
    session,
    setSession,
    isAdmin,
    load,
  };
}

function oauthErrorMessage(error, providerLabel) {
  if (error.status === 403) {
    return 'You signed in successfully, but sales portal access has not been enabled for this account.';
  }
  if (error.code === 'auth/popup-closed-by-user') {
    return `${providerLabel} sign-in was cancelled.`;
  }
  if (error.code === 'auth/popup-blocked') {
    return `Please allow popups for ${providerLabel} sign-in.`;
  }
  if (error.code === 'auth/unauthorized-domain') {
    return 'Firebase has not authorized this domain for sign-in. Use localhost for local OAuth, or add this host in Firebase Auth authorized domains.';
  }
  return error.body?.message || error.message || `Could not sign in with ${providerLabel}.`;
}

function shouldUseLocalhostForFirebaseAuth() {
  return import.meta.env.DEV && window.location.hostname === '127.0.0.1';
}

function redirectToLocalhostForFirebaseAuth() {
  const url = new URL(window.location.href);
  url.hostname = 'localhost';
  window.location.replace(url.toString());
}

function Login({ onLogin, error, setError }) {
  const [busyProvider, setBusyProvider] = useState('');
  const authExchangeStartedRef = useRef(false);

  const finishFirebaseSignIn = useCallback(async (firebaseUser) => {
    const idToken = await firebaseUser.getIdToken();
    const salesSession = await salesApi.login({ idToken });

    saveToken(salesSession.token);
    onLogin(salesSession);
  }, [onLogin]);

  const completeFirebaseUserLogin = useCallback(async (firebaseUser, busyLabel, errorProviderLabel) => {
    if (!firebaseUser || getToken() || authExchangeStartedRef.current) return;

    authExchangeStartedRef.current = true;
    setBusyProvider(busyLabel);
    setError('');

    try {
      await finishFirebaseSignIn(firebaseUser);
    } catch (err) {
      authExchangeStartedRef.current = false;
      if (err.status === 403 && auth) {
        await signOut(auth).catch(() => {});
      }
      setError(oauthErrorMessage(err, errorProviderLabel));
    } finally {
      setBusyProvider('');
    }
  }, [finishFirebaseSignIn, setError]);

  useEffect(() => {
    if (shouldUseLocalhostForFirebaseAuth()) {
      redirectToLocalhostForFirebaseAuth();
    }
  }, []);

  useEffect(() => {
    if (!auth) return undefined;

    let cancelled = false;

    async function completeRedirectLogin() {
      try {
        const result = await getRedirectResult(auth);
        if (!result?.user || cancelled) return;

        await completeFirebaseUserLogin(result.user, 'redirect', 'Firebase');
      } catch (err) {
        if (err.status === 403 && auth) {
          await signOut(auth).catch(() => {});
        }
        if (!cancelled) {
          setError(oauthErrorMessage(err, 'Firebase'));
        }
      } finally {
        if (!cancelled) {
          setBusyProvider('');
        }
      }
    }

    completeRedirectLogin();

    return () => {
      cancelled = true;
    };
  }, [completeFirebaseUserLogin, setError]);

  useEffect(() => {
    if (!auth) return undefined;

    return onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || getToken() || authExchangeStartedRef.current) return;
      await completeFirebaseUserLogin(currentUser, 'redirect', 'Firebase');
    });
  }, [completeFirebaseUserLogin]);

  async function signInWithProvider(providerKey) {
    const providerLabel = providerKey === 'apple' ? 'Apple' : 'Google';
    const provider = providerKey === 'apple' ? appleProvider : googleProvider;

    setBusyProvider(providerKey);
    setError('');

    try {
      if (!auth) {
        throw new Error('Sales portal Firebase sign-in is not configured for this environment.');
      }

      const result = await signInWithPopup(auth, provider);
      await completeFirebaseUserLogin(result.user, providerKey, providerLabel);
    } catch (err) {
      if (err.code === 'auth/popup-blocked' && auth) {
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectError) {
          setError(oauthErrorMessage(redirectError, providerLabel));
        }
        return;
      }
      if (err.status === 403 && auth) {
        await signOut(auth).catch(() => {});
      }
      setError(oauthErrorMessage(err, providerLabel));
    } finally {
      setBusyProvider('');
    }
  }

  return (
    <main className="min-h-screen grid place-items-center px-4 py-10">
      <section className="panel w-full max-w-md p-6 grid gap-5">
        <div className="flex items-center gap-3">
          <img src="/logos/Flock_Icon.svg" alt="Flock" className="h-11 w-11" />
          <div>
            <p className="text-sm font-bold text-teal-700">sales.flock.church</p>
            <h1 className="text-2xl font-black text-[#172026]">Sales Portal</h1>
          </div>
        </div>

        {error ? <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">{error}</div> : null}

        <button
          className="oauth-button"
          disabled={Boolean(busyProvider)}
          type="button"
          onClick={() => signInWithProvider('google')}
        >
          <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {busyProvider === 'google' ? 'Opening Google...' : 'Continue with Google'}
        </button>

        <button
          className="oauth-button apple"
          disabled={Boolean(busyProvider)}
          type="button"
          onClick={() => signInWithProvider('apple')}
        >
          <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          {busyProvider === 'apple' ? 'Opening Apple...' : 'Continue with Apple'}
        </button>

        <p className="text-sm font-semibold leading-6 text-[#5f6b66]">
          Use the same Google or Apple account you use for Flock. Sales access is checked after sign-in.
        </p>
      </section>
    </main>
  );
}

function Sidebar({ activeTab, setActiveTab, profile, onRefresh, onLogout, loading }) {
  const visibleTabs = profile?.role === 'sales_rep' ? tabs.filter((tab) => tab.id !== 'admin') : tabs;
  return (
    <aside className="sidebar">
      <div className="mb-7 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <img src="/logos/Flock_Icon.svg" alt="Flock" className="h-10 w-10" />
          <div>
            <p className="text-xs font-bold text-[#f4b942]">Flock</p>
            <h1 className="text-xl font-black">Sales</h1>
          </div>
        </div>
        <button className="ghost-button !min-h-[44px] !px-3" title="Refresh" type="button" onClick={onRefresh}>
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <nav className="desktop-tabs grid gap-2">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} type="button" className={`tab-button ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <Icon className="h-5 w-5" />
              <span className="font-bold">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="mobile-tabs">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} type="button" className={`tab-button ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <Icon className="h-5 w-5" />
              <span className="font-bold">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-7 panel-muted p-3 text-[#172026]">
        <p className="text-xs font-bold uppercase text-[#5f6b66]">Signed in</p>
        <p className="mt-1 break-words font-black">{profile?.display_name || profile?.name || profile?.email}</p>
        <p className="text-sm capitalize">{profile?.role?.replace('_', ' ')}</p>
      </div>

      <button className="mt-4 ghost-button w-full" type="button" onClick={onLogout}>
        <ArrowRightOnRectangleIcon className="h-5 w-5" />
        Sign out
      </button>
    </aside>
  );
}

function Header({ title, subtitle, children }) {
  return (
    <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <p className="text-sm font-extrabold uppercase text-teal-700">Sales Portal</p>
        <h2 className="text-3xl font-black text-[#172026]">{title}</h2>
        {subtitle ? <p className="mt-1 text-[#5f6b66]">{subtitle}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap gap-2">{children}</div> : null}
    </div>
  );
}

function Today({ data, setActiveTab, onCopy }) {
  const leadsByStatus = data.summary?.leadsByStatus || {};
  const chartData = Object.entries({ new: 0, contacted: 0, nurture: 0, handoff: 0, converted: 0, ...leadsByStatus })
    .map(([name, count]) => ({ name, count }));
  const chartMax = Math.max(1, ...chartData.map((item) => item.count));
  const referral = data.me?.profile?.referral_slug ? referralUrl(data.me.profile.referral_slug) : '';
  const workbenchLeads = data.workbench?.mine?.length ? data.workbench.mine : data.workbench?.available;
  const nextLeads = (workbenchLeads?.length ? workbenchLeads : data.leads).slice(0, 5);

  return (
    <>
      <Header title="Today" subtitle="Pipeline, referrals, and next touches in one place">
        {referral ? (
          <button className="secondary-button" type="button" onClick={() => onCopy(referral)}>
            <ClipboardDocumentIcon className="h-5 w-5" />
            Copy referral link
          </button>
        ) : null}
      </Header>

      <section className="grid gap-4 lg:grid-cols-4">
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">New</span>
          <strong>{leadsByStatus.new || 0}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Touched</span>
          <strong>{(leadsByStatus.contacted || 0) + (leadsByStatus.nurture || 0)}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Pending</span>
          <strong>{formatMoney(data.summary?.pendingCommissionCents || 0)}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Unread</span>
          <strong>{data.notifications.filter((item) => !item.read_at).length}</strong>
        </div>
      </section>

      <section className="mt-4 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="panel p-4">
          <div className="grid h-64 grid-cols-5 items-end gap-3">
            {chartData.map((item) => (
              <div key={item.name} className="grid h-full items-end gap-2">
                <div className="flex h-full items-end rounded-[8px] bg-[#f2eadc]">
                  <div
                    className="w-full rounded-[8px] bg-[#0f766e]"
                    style={{ height: `${Math.max(8, (item.count / chartMax) * 100)}%` }}
                    title={`${item.name}: ${item.count}`}
                  />
                </div>
                <div className="text-center text-xs font-bold capitalize text-[#5f6b66]">{item.name}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-black">Next Leads</h3>
            <button className="ghost-button" type="button" onClick={() => setActiveTab('leads')}>Open leads</button>
          </div>
          <div className="grid gap-2">
            {nextLeads.map((lead) => (
              <div key={lead.id} className="panel-muted p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-black">{lead.canonical_name}</p>
                    <p className="text-sm text-[#5f6b66]">{[lead.city, lead.state].filter(Boolean).join(', ') || 'Location open'}</p>
                  </div>
                  <span className={`status-pill ${statusClass(lead.queue_type)}`}>{queueLabels[lead.queue_type] || lead.queue_type}</span>
                </div>
              </div>
            ))}
            {nextLeads.length === 0 ? <p className="text-sm font-semibold text-[#5f6b66]">No leads in this queue.</p> : null}
          </div>
        </div>
      </section>
    </>
  );
}

const availableFilters = [
  { id: 'all', label: 'All' },
  { id: 'founder', label: 'Popular' },
  { id: 'warm', label: 'Medium' },
  { id: 'standard', label: 'Low' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const DEFAULT_POOL_QUERY = { q: '', queue: 'all', state: '', sort: 'rank' };
const POOL_PAGE_SIZE = 50;

const poolSortOptions = [
  { id: 'rank', label: 'Best match' },
  { id: 'signals', label: 'Most signals' },
  { id: 'last_seen', label: 'Recently seen' },
  { id: 'name', label: 'Name A-Z' },
];

const mineFilters = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'nurture', label: 'Nurture' },
];

function QueueFilter({ options, active, counts, onChange }) {
  return (
    <div className="queue-filters" role="tablist" aria-label="Lead filters">
      {options.map((option) => (
        <button
          key={option.id}
          className={`queue-filter ${active === option.id ? 'active' : ''}`}
          type="button"
          onClick={() => onChange(option.id)}
        >
          <span>{option.label}</span>
          <strong>{counts?.[option.id] || 0}</strong>
        </button>
      ))}
    </div>
  );
}

function WorkbenchLeadCard({ lead, selected, loading, action, actionLabel, actionBusy, onSelect, onAction }) {
  return (
    <article
      aria-selected={selected}
      className={`queue-card ${selected ? 'active' : ''}`}
      onClick={() => onSelect(lead.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(lead.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="queue-card-main">
        <div>
          <h3>{lead.canonical_name}</h3>
          <p>{[lead.city, lead.state].filter(Boolean).join(', ') || 'Location open'}</p>
        </div>
        <button
          className={action}
          disabled={actionBusy}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onAction(lead.id);
          }}
        >
          {actionLabel}
        </button>
      </div>
      <div className="queue-card-meta">
        <span className={`status-pill ${statusClass(lead.queue_type)}`}>{queueLabels[lead.queue_type] || lead.queue_type}</span>
        <span className="status-pill">{lead.status}</span>
        <span className="status-pill">{lead.signal_count || 0} signals</span>
      </div>
      {loading ? <p className="queue-card-loading">Opening quick look</p> : null}
    </article>
  );
}

// One dense line per pool lead: click anywhere = quick look (right panel keeps
// "Open full page" / "Add to queue"); the + claims straight into My Queue.
function PoolLeadRow({ lead, selected, busy, onSelect, onClaim }) {
  return (
    <div
      aria-selected={selected}
      className={`pool-row ${selected ? 'active' : ''}`}
      onClick={() => onSelect(lead.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(lead.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className={`pool-row-dot ${statusClass(lead.queue_type)}`} title={queueLabels[lead.queue_type] || lead.queue_type} />
      <span className="pool-row-name">{lead.canonical_name}</span>
      <span className="pool-row-place">{[lead.city, lead.state].filter(Boolean).join(', ') || 'Location open'}</span>
      <span className="pool-row-signals" title="Demand signals">{lead.signal_count || 0}</span>
      <button
        aria-label={`Add ${lead.canonical_name} to queue`}
        className="pool-row-add"
        disabled={busy}
        title="Add to queue"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClaim(lead.id);
        }}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function ManualLeadForm({ onCreate }) {
  const [lead, setLead] = useState({ canonicalName: '', city: '', state: '', queueType: 'standard' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submitManualLead(event) {
    event.preventDefault();
    if (!lead.canonicalName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await onCreate({
        canonicalName: lead.canonicalName,
        city: lead.city,
        state: lead.state,
        queueType: lead.queueType,
      });
      setLead({ canonicalName: '', city: '', state: '', queueType: 'standard' });
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not create lead');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel p-4">
      <form className="manual-lead-form" onSubmit={submitManualLead}>
        <h2>Create lead</h2>
        <input
          className="field"
          placeholder="Church name"
          value={lead.canonicalName}
          onChange={(event) => setLead({ ...lead, canonicalName: event.target.value })}
        />
        <input
          className="field"
          placeholder="City"
          value={lead.city}
          onChange={(event) => setLead({ ...lead, city: event.target.value })}
        />
        <input
          className="field"
          placeholder="State"
          value={lead.state}
          onChange={(event) => setLead({ ...lead, state: event.target.value })}
        />
        <select className="field" value={lead.queueType} onChange={(event) => setLead({ ...lead, queueType: event.target.value })}>
          <option value="standard">Low</option>
          <option value="warm">Medium</option>
          <option value="founder">Popular</option>
        </select>
        <button className="primary-button" type="submit" disabled={submitting || !lead.canonicalName.trim()}>
          <PlusIcon className="h-5 w-5" />
          {submitting ? 'Creating...' : 'Create'}
        </button>
      </form>
      {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
    </section>
  );
}

function SalesRepWorkbench({ workbench, onOpenLead, onRefreshLeads, onManualLead }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [loadingLeadId, setLoadingLeadId] = useState('');
  const [busyLeadId, setBusyLeadId] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [mineFilter, setMineFilter] = useState('all');
  // Pool browsing is server-side (search/filter/sort/paging) — the pool can be
  // thousands of leads, far past what the workbench payload carries.
  const [poolQuery, setPoolQuery] = useState(DEFAULT_POOL_QUERY);
  const [pool, setPool] = useState({ leads: [], total: 0, hasMore: false });
  const [poolLoading, setPoolLoading] = useState(true);
  const [poolError, setPoolError] = useState('');
  const poolFetchSeq = useRef(0);
  const detailPanelRef = useRef(null);

  const isDefaultPool = poolQuery.q === DEFAULT_POOL_QUERY.q
    && poolQuery.queue === DEFAULT_POOL_QUERY.queue
    && poolQuery.state === DEFAULT_POOL_QUERY.state
    && poolQuery.sort === DEFAULT_POOL_QUERY.sort;

  const fetchPool = useCallback(async (query, { append = false, offset = 0 } = {}) => {
    const seq = ++poolFetchSeq.current;
    setPoolLoading(true);
    setPoolError('');
    try {
      const data = await salesApi.availableLeads({
        q: query.q || undefined,
        queue: query.queue === 'all' ? undefined : query.queue,
        state: query.state || undefined,
        sort: query.sort === 'rank' ? undefined : query.sort,
        limit: POOL_PAGE_SIZE,
        offset,
      });
      if (seq !== poolFetchSeq.current) return; // superseded by a newer fetch
      setPool((prev) => ({
        leads: append ? [...prev.leads, ...(data.leads || [])] : (data.leads || []),
        total: data.total || 0,
        hasMore: Boolean(data.hasMore),
      }));
    } catch (err) {
      if (seq === poolFetchSeq.current) {
        setPoolError(err.body?.message || err.message || 'Could not load available leads');
      }
    } finally {
      if (seq === poolFetchSeq.current) setPoolLoading(false);
    }
  }, []);

  // Debounced refetch on any control change; also performs the initial load.
  useEffect(() => {
    const handle = setTimeout(() => { fetchPool(poolQuery); }, 300);
    return () => clearTimeout(handle);
  }, [poolQuery, fetchPool]);

  function loadMorePool() {
    fetchPool(poolQuery, { append: true, offset: pool.leads.length });
  }

  const mine = workbench?.mine || [];
  const filteredMine = mineFilter === 'all'
    ? mine
    : mine.filter((lead) => lead.status === mineFilter);

  async function loadLead(leadId, { scroll = false } = {}) {
    setLoadingLeadId(leadId);
    setError('');
    try {
      const detail = await salesApi.lead(leadId);
      setSelectedLead(detail);
      if (scroll) {
        window.requestAnimationFrame(() => {
          detailPanelRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }
      return detail;
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load lead');
      return null;
    } finally {
      setLoadingLeadId('');
    }
  }

  async function claim(leadId) {
    setBusyLeadId(leadId);
    setError('');
    try {
      await salesApi.claimLead(leadId);
      // Drop the claimed lead from the loaded pool in place — a full refetch
      // would reset "Load more" progress and scroll position.
      setPool((prev) => ({
        ...prev,
        leads: prev.leads.filter((lead) => lead.id !== leadId),
        total: Math.max(0, prev.total - 1),
      }));
      await onRefreshLeads();
      await loadLead(leadId, { scroll: true });
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not add lead to your queue');
    } finally {
      setBusyLeadId('');
    }
  }

  async function release(leadId) {
    setBusyLeadId(leadId);
    setError('');
    try {
      await salesApi.releaseLead(leadId);
      setSelectedLead(null);
      await Promise.all([onRefreshLeads(), fetchPool(poolQuery)]);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not release lead');
    } finally {
      setBusyLeadId('');
    }
  }

  async function updateStatus(status) {
    const leadId = selectedLead?.lead?.id;
    if (!leadId) return;
    setBusyLeadId(leadId);
    setError('');
    try {
      await salesApi.updateLeadStatus(leadId, status);
      await Promise.all([loadLead(leadId), onRefreshLeads()]);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not update lead');
    } finally {
      setBusyLeadId('');
    }
  }

  async function submitNote(event) {
    event.preventDefault();
    const leadId = selectedLead?.lead?.id;
    if (!leadId || !note.trim()) return;
    setBusyLeadId(leadId);
    setError('');
    try {
      await salesApi.createLeadNote(leadId, note);
      setNote('');
      await Promise.all([loadLead(leadId), onRefreshLeads()]);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not add note');
    } finally {
      setBusyLeadId('');
    }
  }

  async function createManualLead(payload) {
    const result = await onManualLead(payload);
    const lead = result?.lead || result;
    if (lead?.id) {
      await loadLead(lead.id, { scroll: true });
    }
  }

  const selected = selectedLead?.lead;
  const selectedIsMine = selected?.assigned_to;
  const hasWork = Boolean(
    selected?.last_contacted_at
      || selectedLead?.notes?.length
      || selectedLead?.activities?.some((activity) => !['claimed', 'released', 'assigned'].includes(activity.activity_type))
  );
  const canRelease = selectedIsMine && selected.status === 'new' && !hasWork;

  return (
    <>
      <Header title="Leads" subtitle="Claim available churches, then work your active queue" />
      {error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</div> : null}
      <ManualLeadForm onCreate={createManualLead} />

      <section className="workbench-layout mt-4">
        <div className="workbench-columns">
          <section className="queue-panel">
            <div className="queue-panel-header">
              <div>
                <p className="queue-eyebrow">Available Pool</p>
                <h2>{workbench?.counts?.available?.all || 0} unclaimed</h2>
              </div>
              <QueueFilter
                options={availableFilters}
                active={poolQuery.queue}
                counts={workbench?.counts?.available}
                onChange={(queue) => setPoolQuery((prev) => ({ ...prev, queue }))}
              />
            </div>

            <div className="pool-controls">
              <input
                className="pool-search"
                placeholder="Search church or city"
                type="search"
                value={poolQuery.q}
                onChange={(event) => setPoolQuery((prev) => ({ ...prev, q: event.target.value }))}
              />
              <select
                aria-label="Filter by state"
                className="pool-select"
                value={poolQuery.state}
                onChange={(event) => setPoolQuery((prev) => ({ ...prev, state: event.target.value }))}
              >
                <option value="">All states</option>
                {US_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
              <select
                aria-label="Sort leads"
                className="pool-select"
                value={poolQuery.sort}
                onChange={(event) => setPoolQuery((prev) => ({ ...prev, sort: event.target.value }))}
              >
                {poolSortOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              {!isDefaultPool ? (
                <button className="ghost-button pool-reset" type="button" onClick={() => setPoolQuery(DEFAULT_POOL_QUERY)}>
                  Reset to top picks
                </button>
              ) : null}
            </div>

            <div className="queue-list pool-list">
              {pool.leads.map((lead) => (
                <PoolLeadRow
                  key={lead.id}
                  busy={busyLeadId === lead.id}
                  lead={lead}
                  selected={selected?.id === lead.id}
                  onClaim={claim}
                  onSelect={(leadId) => loadLead(leadId, { scroll: true })}
                />
              ))}
              {!poolLoading && !poolError && pool.leads.length === 0
                ? <p className="queue-empty">No available leads match.</p>
                : null}
              {poolLoading && pool.leads.length === 0 ? <p className="queue-empty">Loading leads...</p> : null}
              {poolError ? <p className="queue-empty pool-error">{poolError}</p> : null}
              {pool.hasMore ? (
                <button className="ghost-button w-full" disabled={poolLoading} type="button" onClick={loadMorePool}>
                  {poolLoading ? 'Loading...' : `Load more (${pool.leads.length} of ${pool.total})`}
                </button>
              ) : null}
            </div>
          </section>

          <section className="queue-panel mine">
            <div className="queue-panel-header">
              <div>
                <p className="queue-eyebrow">My Queue</p>
                <h2>{workbench?.counts?.mine?.all || 0} active</h2>
              </div>
              <QueueFilter
                options={mineFilters}
                active={mineFilter}
                counts={workbench?.counts?.mine}
                onChange={setMineFilter}
              />
            </div>
            <div className="queue-list">
              {filteredMine.map((lead) => (
                <WorkbenchLeadCard
                  key={lead.id}
                  lead={lead}
                  selected={selected?.id === lead.id}
                  loading={loadingLeadId === lead.id}
                  action="queue-action release"
                  actionLabel={loadingLeadId === lead.id ? 'Opening...' : 'Open'}
                  actionBusy={loadingLeadId === lead.id}
                  onSelect={(leadId) => loadLead(leadId, { scroll: true })}
                  onAction={(leadId) => loadLead(leadId, { scroll: true })}
                />
              ))}
              {filteredMine.length === 0 ? <p className="queue-empty">Nothing in this queue yet.</p> : null}
            </div>
          </section>
        </div>

        <aside ref={detailPanelRef} className="panel p-4">
          {selected ? (
            <div className="grid gap-4">
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-black">{selected.canonical_name}</h3>
                    <p className="text-sm text-[#5f6b66]">{[selected.city, selected.state].filter(Boolean).join(', ') || 'Location open'}</p>
                  </div>
                  <button className="ghost-button !px-3" title="Close quick look" type="button" onClick={() => setSelectedLead(null)}>x</button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`status-pill ${statusClass(selected.queue_type)}`}>{queueLabels[selected.queue_type] || selected.queue_type}</span>
                  <span className="status-pill">{selected.status}</span>
                  <span className="status-pill">{selected.unique_user_count || 0} seekers</span>
                </div>
              </div>

              <button className="primary-button w-full" type="button" onClick={() => onOpenLead(selected.id)}>
                <DocumentTextIcon className="h-5 w-5" />
                Open full page
              </button>

              {!selectedIsMine ? (
                <button className="primary-button w-full" type="button" disabled={busyLeadId === selected.id} onClick={() => claim(selected.id)}>
                  <PlusIcon className="h-5 w-5" />
                  {busyLeadId === selected.id ? 'Adding...' : 'Add to queue'}
                </button>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {statusActions.map((action) => {
                      const Icon = action.icon;
                      const isActive = selected.status === action.status;
                      return (
                        <button
                          key={action.status}
                          aria-pressed={isActive}
                          className={statusActionClass(selected.status, action.status)}
                          disabled={busyLeadId === selected.id}
                          type="button"
                          onClick={() => updateStatus(action.status)}
                        >
                          <Icon className="h-5 w-5" />
                          {action.label}
                        </button>
                      );
                    })}
                  </div>

                  <button className="ghost-button w-full" type="button" disabled={!canRelease || busyLeadId === selected.id} onClick={() => release(selected.id)}>
                    <ArrowLeftIcon className="h-5 w-5" />
                    {busyLeadId === selected.id ? 'Releasing...' : 'Release to available'}
                  </button>
                  {!canRelease ? <p className="text-xs font-bold text-[#5f6b66]">Worked leads stay in your queue until they are closed or reassigned by an admin.</p> : null}
                </>
              )}

              {selectedIsMine ? (
                <form className="grid gap-2" onSubmit={submitNote}>
                  <textarea className="field min-h-[96px]" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note" />
                  <button className="primary-button" disabled={busyLeadId === selected.id} type="submit">Add note</button>
                </form>
              ) : null}

              <DemandSignalsCompact summary={selectedLead.signalSummary} />
            </div>
          ) : (
            <p className="text-sm font-semibold text-[#5f6b66]">Select a lead for a quick look.</p>
          )}
        </aside>
      </section>
    </>
  );
}

function Leads({ leads, workbench, onOpenLead, isAdmin, reps, onRefreshLeads, onManualLead }) {
  if (!isAdmin) {
    return (
      <SalesRepWorkbench
        workbench={workbench}
        onOpenLead={onOpenLead}
        onRefreshLeads={onRefreshLeads}
        onManualLead={onManualLead}
      />
    );
  }

  return (
    <AdminLeadsTable
      leads={leads}
      onOpenLead={onOpenLead}
      isAdmin={isAdmin}
      reps={reps}
      onRefreshLeads={onRefreshLeads}
      onManualLead={onManualLead}
    />
  );
}

function AdminLeadsTable({ leads, onOpenLead, isAdmin, reps, onRefreshLeads, onManualLead }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [loadingLeadId, setLoadingLeadId] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  // null = server order (status/priority); first click sorts ascending, second flips.
  const [sort, setSort] = useState(null);
  const detailPanelRef = useRef(null);

  const sortedLeads = useMemo(() => sortLeads(leads, sort), [leads, sort]);

  function toggleSort(key) {
    setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  async function loadLead(leadId, { scroll = false } = {}) {
    setLoadingLeadId(leadId);
    setError('');
    try {
      const detail = await salesApi.lead(leadId);
      setSelectedLead(detail);
      setAssignee(detail.lead?.assigned_to || '');

      if (scroll) {
        window.requestAnimationFrame(() => {
          detailPanelRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }

      return detail;
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load lead');
      return null;
    } finally {
      setLoadingLeadId('');
    }
  }

  async function submitNote(event) {
    event.preventDefault();
    const leadId = selectedLead?.lead?.id;
    if (!leadId || !note.trim()) return;
    await salesApi.createLeadNote(leadId, note);
    setNote('');
    await loadLead(leadId);
  }

  async function updateStatus(status) {
    const leadId = selectedLead?.lead?.id;
    if (!leadId) return;
    await salesApi.updateLeadStatus(leadId, status);
    await Promise.all([loadLead(leadId), onRefreshLeads()]);
  }

  async function assignLead() {
    const leadId = selectedLead?.lead?.id;
    if (!leadId) return;
    await salesApi.assignLead(leadId, assignee || null);
    await Promise.all([loadLead(leadId), onRefreshLeads()]);
  }

  async function createManualLead(payload) {
    const result = await onManualLead(payload);
    const lead = result?.lead || result;
    if (lead?.id) {
      await loadLead(lead.id, { scroll: true });
    }
  }

  const selected = selectedLead?.lead;

  return (
    <>
      <Header title="Leads" subtitle={`${leads.length} active church opportunities`} />
      {error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</div> : null}
      <ManualLeadForm onCreate={createManualLead} />

      <section className="leads-layout mt-4">
        <div className="panel table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {leadSortColumns.map((col) => {
                  const active = sort?.key === col.key;
                  return (
                    <th key={col.key} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                      <button className="sort-button" type="button" onClick={() => toggleSort(col.key)}>
                        {col.label}
                        {active ? (
                          sort.dir === 'asc' ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />
                        ) : null}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedLeads.map((lead) => (
                <tr
                  key={lead.id}
                  aria-selected={selected?.id === lead.id}
                  className={`lead-row ${selected?.id === lead.id ? 'active' : ''}`}
                  onClick={() => loadLead(lead.id, { scroll: true })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      loadLead(lead.id, { scroll: true });
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <td>
                    <strong>{lead.canonical_name}</strong>
                    <div className="text-sm text-[#5f6b66]">{[lead.city, lead.state].filter(Boolean).join(', ')}</div>
                    {loadingLeadId === lead.id ? <div className="text-xs font-bold text-[#0f766e]">Opening quick look</div> : null}
                  </td>
                  <td><span className={`status-pill ${statusClass(lead.queue_type)}`}>{queueLabels[lead.queue_type] || lead.queue_type}</span></td>
                  <td>{lead.signal_count}</td>
                  <td><span className={`status-pill ${statusClass(lead.status)}`}>{lead.status}</span></td>
                  <td>{leadLastSeenLabel(lead)}</td>
                  <td>{formatDate(lead.next_action_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside ref={detailPanelRef} className="panel p-4">
          {selected ? (
            <div className="grid gap-4">
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-black">{selected.canonical_name}</h3>
                    <p className="text-sm text-[#5f6b66]">{[selected.city, selected.state].filter(Boolean).join(', ') || 'Location open'}</p>
                  </div>
                  <button className="ghost-button !px-3" title="Close quick look" type="button" onClick={() => setSelectedLead(null)}>x</button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`status-pill ${statusClass(selected.queue_type)}`}>{queueLabels[selected.queue_type] || selected.queue_type}</span>
                  <span className="status-pill">{selected.status}</span>
                  <span className="status-pill">{selected.unique_user_count || 0} seekers</span>
                </div>
              </div>

              <button className="primary-button w-full" type="button" onClick={() => onOpenLead(selected.id)}>
                <DocumentTextIcon className="h-5 w-5" />
                Open full page
              </button>

              <div className="grid grid-cols-2 gap-2">
                {statusActions.map((action) => {
                  const Icon = action.icon;
                  const isActive = selected.status === action.status;
                  return (
                    <button
                      key={action.status}
                      aria-pressed={isActive}
                      className={statusActionClass(selected.status, action.status)}
                      type="button"
                      onClick={() => updateStatus(action.status)}
                    >
                      <Icon className="h-5 w-5" />
                      {action.label}
                    </button>
                  );
                })}
              </div>

              {isAdmin ? (
                <div className="panel-muted p-3">
                  <label className="grid gap-2 text-sm font-bold">
                    Assign
                    <select className="field" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
                      <option value="">Unassigned</option>
                      {reps.map((rep) => (
                        <option key={rep.user_id} value={rep.user_id}>{rep.display_name || rep.name || rep.email}</option>
                      ))}
                    </select>
                  </label>
                  <button className="primary-button mt-2 w-full" type="button" onClick={assignLead}>Save assignment</button>
                </div>
              ) : null}

              <form className="grid gap-2" onSubmit={submitNote}>
                <textarea className="field min-h-[96px]" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note" />
                <button className="primary-button" type="submit">Add note</button>
              </form>

              <DemandSignalsCompact summary={selectedLead.signalSummary} />
            </div>
          ) : (
            <p className="text-sm font-semibold text-[#5f6b66]">Select a lead for a quick look.</p>
          )}
        </aside>
      </section>
    </>
  );
}

function LeadDetailPage({ leadId, isAdmin, reps, onBack, onRefreshLeads }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLead = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const nextDetail = await salesApi.lead(leadId);
      setDetail(nextDetail);
      setAssignee(nextDetail.lead?.assigned_to || '');
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load lead');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  async function updateStatus(status) {
    await salesApi.updateLeadStatus(leadId, status);
    await Promise.all([loadLead(), onRefreshLeads()]);
  }

  async function submitNote(event) {
    event.preventDefault();
    if (!note.trim()) return;
    await salesApi.createLeadNote(leadId, note);
    setNote('');
    await loadLead();
  }

  async function assignLead() {
    await salesApi.assignLead(leadId, assignee || null);
    await Promise.all([loadLead(), onRefreshLeads()]);
  }

  const lead = detail?.lead;

  return (
    <>
      <div className="mb-4">
        <button className="ghost-button" type="button" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
          Back to leads
        </button>
      </div>

      <Header title={lead?.canonical_name || 'Lead detail'} subtitle={lead ? [lead.city, lead.state].filter(Boolean).join(', ') || 'Location open' : 'Loading church opportunity'} />

      {error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</div> : null}

      {loading ? (
        <section className="panel p-5">
          <p className="font-bold text-[#5f6b66]">Loading lead...</p>
        </section>
      ) : lead ? (
        <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="grid gap-4">
            <div className="panel p-5">
              <div className="flex flex-wrap gap-2">
                <span className={`status-pill ${statusClass(lead.queue_type)}`}>{queueLabels[lead.queue_type] || lead.queue_type}</span>
                <span className="status-pill">{lead.status}</span>
                <span className="status-pill">{lead.trust_level || 'unrated'}</span>
                <span className="status-pill">{lead.unique_user_count || 0} seekers</span>
              </div>
              <dl className="mt-5 grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-black uppercase text-[#5f6b66]">Signals</dt>
                  <dd className="text-2xl font-black">{lead.signal_count || 0}</dd>
                </div>
                <div>
                  <dt className="text-xs font-black uppercase text-[#5f6b66]">Next action</dt>
                  <dd className="text-lg font-black">{formatDate(lead.next_action_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-black uppercase text-[#5f6b66]">Website</dt>
                  <dd className="font-bold">{lead.website || 'Open'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-black uppercase text-[#5f6b66]">Phone</dt>
                  <dd className="font-bold">{lead.phone || 'Open'}</dd>
                </div>
              </dl>
            </div>

            <DemandSignalsSection
              summary={detail.signalSummary}
              leadName={lead.canonical_name}
              isAdmin={isAdmin}
              sources={detail.sources || []}
            />

            <div className="panel p-5">
              <h3 className="mb-3 text-lg font-black">Notes</h3>
              <div className="grid gap-2">
                {detail.notes?.map((item) => (
                  <div key={item.id} className="panel-muted p-3 text-sm">
                    <p>{item.body}</p>
                    <p className="mt-1 text-[#5f6b66]">{item.author_name} - {formatDate(item.created_at)}</p>
                  </div>
                ))}
                {detail.notes?.length ? null : <p className="text-sm font-semibold text-[#5f6b66]">No notes yet.</p>}
              </div>
            </div>
          </div>

          <aside className="grid gap-4 content-start">
            <div className="panel p-4">
              <h3 className="mb-3 text-lg font-black">Actions</h3>
              <div className="grid gap-2">
                {statusActions.map((action) => {
                  const Icon = action.icon;
                  const isActive = lead.status === action.status;
                  return (
                    <button
                      key={action.status}
                      aria-pressed={isActive}
                      className={statusActionClass(lead.status, action.status)}
                      type="button"
                      onClick={() => updateStatus(action.status)}
                    >
                      <Icon className="h-5 w-5" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {isAdmin ? (
              <div className="panel p-4">
                <h3 className="mb-3 text-lg font-black">Assignment</h3>
                <label className="grid gap-2 text-sm font-bold">
                  Rep
                  <select className="field" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
                    <option value="">Unassigned</option>
                    {reps.map((rep) => (
                      <option key={rep.user_id} value={rep.user_id}>{rep.display_name || rep.name || rep.email}</option>
                    ))}
                  </select>
                </label>
                <button className="primary-button mt-2 w-full" type="button" onClick={assignLead}>Save assignment</button>
              </div>
            ) : null}

            <form className="panel grid gap-2 p-4" onSubmit={submitNote}>
              <h3 className="text-lg font-black">Add note</h3>
              <textarea className="field min-h-[112px]" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note" />
              <button className="primary-button" type="submit">Add note</button>
            </form>
          </aside>
        </section>
      ) : null}
    </>
  );
}

function DemandSignalsCompact({ summary }) {
  const groups = summary?.groups || [];

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="font-black">Demand Signals</h4>
          <p className="text-xs font-bold text-[#5f6b66]">Plain-language app interest</p>
        </div>
        <span className="status-pill">{signalTotalLabel(summary)}</span>
      </div>
      {groups.slice(0, 3).map((group) => (
        <details key={group.key} className="signal-group compact" open={groups.length === 1}>
          <summary>
            <div>
              <h4>{group.label}</h4>
              <p>{group.sourceLabel} - Latest {formatSignalDate(group.latestSeenAt)}</p>
            </div>
            <span className="signal-summary-actions">
              <span className="status-pill">{group.countLabel}</span>
              <span className={seekerPillClass(group)}>{seekerCountLabel(group)}</span>
            </span>
          </summary>
          <div className="signal-group-body">
            {group.people?.length > 0 ? (
              <div className="grid gap-2">
                <h5>Named seekers</h5>
                {group.people.map((person) => {
                  const firstName = firstNameFromDisplayName(person.name);
                  return (
                    <div key={person.key} className="signal-person">
                      <div>
                        <strong>{person.name}</strong>
                        {person.email ? <p>{person.email}</p> : null}
                      </div>
                      <span className="status-pill">{person.count} {person.count === 1 ? 'signal' : 'signals'}</span>
                      <p>Associate cue: {firstName} searched for "{group.label}".</p>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {group.anonymousCount > 0 ? (
              <p className="signal-anonymous">{anonymousSignalText(group)}</p>
            ) : null}
          </div>
        </details>
      ))}
      {groups.length === 0 ? <p className="text-sm font-semibold text-[#5f6b66]">No demand signals recorded yet.</p> : null}
    </div>
  );
}

function DemandSignalsSection({ summary, leadName, isAdmin, sources }) {
  const groups = summary?.groups || [];

  return (
    <div className="panel p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-black">Demand Signals</h3>
          <p className="text-sm font-semibold text-[#5f6b66]">Plain-language app interest reps can use before reaching out</p>
        </div>
        <span className="status-pill">{signalTotalLabel(summary)}</span>
      </div>

      <dl className="mb-4 grid gap-3 sm:grid-cols-4">
        <div className="signal-stat">
          <dt>Total interest</dt>
          <dd>{signalTotalLabel(summary)}</dd>
        </div>
        <div className="signal-stat">
          <dt>Named seekers</dt>
          <dd>{summary?.namedSeekerCount || 0}</dd>
        </div>
        <div className="signal-stat">
          <dt>Latest</dt>
          <dd>{formatSignalDate(summary?.latestSeenAt)}</dd>
        </div>
        <div className="signal-stat">
          <dt>First seen</dt>
          <dd>{formatSignalDate(summary?.firstSeenAt)}</dd>
        </div>
      </dl>

      <div className="grid gap-3">
        {groups.map((group) => (
          <details key={group.key} className="signal-group">
            <summary>
              <div>
                <h4>{group.label}</h4>
                <p>{group.sourceLabel} - Latest {formatSignalDate(group.latestSeenAt)}</p>
              </div>
              <span className="signal-summary-actions">
                <span className="status-pill">{group.countLabel}</span>
                <span className={seekerPillClass(group)}>{seekerCountLabel(group)}</span>
              </span>
            </summary>
            <div className="signal-group-body">
              {group.people.length > 0 ? (
                <div className="grid gap-2">
                  <h5>Named seekers</h5>
                  {group.people.map((person) => {
                    const firstName = firstNameFromDisplayName(person.name);
                    return (
                      <div key={person.key} className="signal-person">
                        <div>
                          <strong>{person.name}</strong>
                          {person.email ? <p>{person.email}</p> : null}
                        </div>
                        <span className="status-pill">{person.count} {person.count === 1 ? 'signal' : 'signals'}</span>
                        <p className="sm:col-span-2">
                          Associate cue: {firstName} searched for "{group.label}" while trying to find {leadName}.
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {group.anonymousCount > 0 ? (
                <p className="signal-anonymous">{anonymousSignalText(group)}</p>
              ) : null}

              <p className="text-sm font-semibold text-[#5f6b66]">
                Use this as demand proof. Name a seeker only when a name or email is shown above.
              </p>
            </div>
          </details>
        ))}

        {groups.length === 0 ? <p className="text-sm font-semibold text-[#5f6b66]">No demand signals recorded yet.</p> : null}
      </div>

      {isAdmin ? (
        <details className="raw-source-log mt-4">
          <summary>Raw source log</summary>
          <div className="mt-3 grid gap-2">
            {sources.map((source) => (
              <div key={source.id} className="panel-muted p-3 text-sm">
                <strong>{source.raw_name || 'Untitled source'}</strong>
                <p className="text-[#5f6b66]">{formatSourceType(source.source_type)} - {formatDate(source.contributed_at)}</p>
                {source.user_name || source.user_email ? (
                  <p className="text-[#5f6b66]">{[source.user_name, source.user_email].filter(Boolean).join(' - ')}</p>
                ) : null}
              </div>
            ))}
            {sources.length ? null : <p className="text-sm font-semibold text-[#5f6b66]">No raw source rows recorded.</p>}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function CommissionProjectionChart({ ledger, schedule, paymentLimit }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const chartPoints = useMemo(
    () => buildCommissionChartPoints({ ledger, schedule, paymentLimit }),
    [ledger, schedule, paymentLimit]
  );

  if (!chartPoints.length) {
    return <p className="text-sm font-semibold text-[#5f6b66]">No commission payments to chart yet.</p>;
  }

  const width = 760;
  const height = 260;
  const padding = { top: 24, right: 26, bottom: 42, left: 54 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxCumulative = Math.max(...chartPoints.map((item) => item.cumulativeCents), 1);
  const visiblePoints = chartPoints.map((item, index) => {
    const x = padding.left + (chartPoints.length === 1 ? chartWidth / 2 : (index / (chartPoints.length - 1)) * chartWidth);
    const y = padding.top + chartHeight - (item.cumulativeCents / maxCumulative) * chartHeight;
    return {
      ...item,
      x,
      y,
      xPct: (x / width) * 100,
      yPct: (y / height) * 100,
    };
  });
  const accruedPoints = visiblePoints.filter((point) => point.kind === 'accrued');
  const projectedPoints = visiblePoints.filter((point) => point.kind === 'projected');
  const projectedLinePoints = accruedPoints.length ? [accruedPoints[accruedPoints.length - 1], ...projectedPoints] : projectedPoints;
  const hoveredPoint = activeIndex == null ? null : visiblePoints[activeIndex];
  const summaryPoint = hoveredPoint || visiblePoints[visiblePoints.length - 1];
  const tooltipClass = hoveredPoint?.xPct > 72 ? 'is-left' : hoveredPoint?.xPct < 28 ? 'is-right' : '';

  return (
    <div className="commission-chart">
      <div className="commission-chart-header">
        <div>
          <strong>{formatMoney(summaryPoint.cumulativeCents)}</strong>
          <span>Cumulative after payment {summaryPoint.paymentNumber}</span>
        </div>
        <div className="commission-chart-legend" aria-label="Payment type legend">
          <span><i className="legend-dot accrued" />Accrued</span>
          <span><i className="legend-dot projected" />Projected</span>
        </div>
      </div>

      <div className="commission-chart-stage">
        <svg className="commission-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Cumulative commission projection">
          {[0, 0.5, 1].map((tick) => {
            const y = padding.top + chartHeight - tick * chartHeight;
            return (
              <g key={tick}>
                <line className="commission-chart-grid" x1={padding.left} x2={width - padding.right} y1={y} y2={y} />
                <text className="commission-chart-axis" x={padding.left - 10} y={y + 4} textAnchor="end">
                  {formatMoney(maxCumulative * tick)}
                </text>
              </g>
            );
          })}

          {accruedPoints.length > 1 ? (
            <polyline className="commission-chart-line accrued" points={pointsToPolyline(accruedPoints)} />
          ) : null}
          {projectedLinePoints.length > 1 ? (
            <polyline className="commission-chart-line projected" points={pointsToPolyline(projectedLinePoints)} />
          ) : null}

          {visiblePoints.map((point, index) => (
            <circle
              key={`${point.kind}-${point.paymentNumber}-${point.date}`}
              tabIndex="0"
              className={`commission-chart-point ${point.kind}`}
              cx={point.x}
              cy={point.y}
              r={summaryPoint.paymentNumber === point.paymentNumber ? 7 : 5}
              role="button"
              aria-label={`Payment ${point.paymentNumber}: ${point.kind}, ${formatMoney(point.amountCents)}, ${formatMoney(point.cumulativeCents)} cumulative`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
            />
          ))}

          <text className="commission-chart-axis strong" x={padding.left} y={height - 10} textAnchor="middle">Payment 1</text>
          <text className="commission-chart-axis strong" x={width - padding.right} y={height - 10} textAnchor="middle">
            Payment {visiblePoints[visiblePoints.length - 1].paymentNumber}
          </text>
        </svg>

        {hoveredPoint ? (
          <div
            className={`commission-chart-tooltip ${tooltipClass}`}
            style={{ left: `${hoveredPoint.xPct}%`, top: `${hoveredPoint.yPct}%` }}
          >
            <strong>Payment {hoveredPoint.paymentNumber}</strong>
            <span>{formatLongDate(hoveredPoint.date)} - {hoveredPoint.kind === 'accrued' ? 'Accrued' : 'Projected'}</span>
            <b>{formatMoney(hoveredPoint.amountCents)}</b>
            <small>{formatMoney(hoveredPoint.cumulativeCents)} cumulative</small>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DealDetailPage({ dealRoute, onBack }) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDeal = useCallback(async () => {
    if (!dealRoute?.organizationId) return;
    setLoading(true);
    setError('');
    try {
      const result = await salesApi.commissionDeal(dealRoute.organizationId, { repUserId: dealRoute.repUserId });
      setDeal(result.deal);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load commission deal');
    } finally {
      setLoading(false);
    }
  }, [dealRoute?.organizationId, dealRoute?.repUserId]);

  useEffect(() => {
    loadDeal();
  }, [loadDeal]);

  const schedule = deal?.projection?.schedule || [];
  const ledger = deal?.ledger || [];
  const projectionAssumptionMonths = deal?.projection?.assumptionMonths || 12;
  const projectionStepMonths = deal?.organization?.billingPeriod === 'annual' ? 12 : 1;
  const paymentLimit = deal?.projection?.assumptionPaymentCount
    || Math.max(1, Math.ceil(Number(deal?.projection?.term?.durationMonths || projectionAssumptionMonths) / projectionStepMonths));
  const paymentNoun = paymentLimit === 1 ? 'payment' : 'payments';
  const projectionNote = deal?.projection?.ongoing
    ? `Shows ${paymentLimit} ${paymentNoun} in a ${periodLabel(deal.organization.billingPeriod).toLowerCase()} forecast, including accrued payments; no projected ledger rows are created.`
    : 'Display-only estimates; no projected ledger rows are created.';
  const emptyProjectionText = deal?.projection?.ongoing
    ? 'No future projected renewals could be estimated.'
    : 'No future projected payments for this term.';

  return (
    <>
      <div className="mb-4">
        <button className="ghost-button" type="button" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
          Back
        </button>
      </div>

      <Header
        title={deal?.organization?.name || 'Commission deal'}
        subtitle={deal ? `${deal.rep.displayName} - ${tierLabel(deal.organization.tierCode)} - ${periodLabel(deal.organization.billingPeriod)}` : 'Loading commission deal'}
      />

      {error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</div> : null}

      {loading ? (
        <section className="panel p-5">
          <p className="font-bold text-[#5f6b66]">Loading commission detail...</p>
        </section>
      ) : deal ? (
        <section className="grid gap-4">
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Accrued</span>
              <strong>{formatMoney(deal.accruedToDateCents)}</strong>
            </div>
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Projected</span>
              <strong>{formatMoney(deal.projection?.projectedRemainingCents || 0)}</strong>
            </div>
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Total potential</span>
              <strong>{formatMoney(deal.projection?.projectedTotalCents || deal.accruedToDateCents)}</strong>
            </div>
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Term</span>
              <strong className="text-lg">{termLabel(deal.projection?.term)}</strong>
            </div>
          </div>

          <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="grid gap-4">
              <div className="panel p-5">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-black">Projection</h3>
                    <p className="text-sm font-semibold text-[#5f6b66]">{projectionNote}</p>
                  </div>
                  <span className={`status-pill ${deal.projection?.term?.expired ? 'hot' : 'warn'}`}>{termLabel(deal.projection?.term)}</span>
                </div>
                {ledger.length || schedule.length ? (
                  <CommissionProjectionChart ledger={ledger} schedule={schedule} paymentLimit={paymentLimit} />
                ) : (
                  <p className="text-sm font-semibold text-[#5f6b66]">{emptyProjectionText}</p>
                )}
              </div>

              <div className="panel table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Invoice</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((item) => (
                      <tr key={item.id}>
                        <td>{item.event_type}</td>
                        <td>{item.stripe_invoice_id || 'Manual'}</td>
                        <td className={Number(item.amount_cents) < 0 ? 'text-red-700 font-bold' : 'font-bold'}>{formatMoney(item.amount_cents)}</td>
                        <td><span className="status-pill">{item.settlement_status || item.status}</span></td>
                        <td>{formatLongDate(item.event_timestamp || item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <aside className="grid gap-4 content-start">
              <div className="panel p-4">
                <h3 className="mb-3 text-lg font-black">Deal</h3>
                <dl className="grid gap-3">
                  <div>
                    <dt className="text-xs font-black uppercase text-[#5f6b66]">Rep</dt>
                    <dd className="font-black">{deal.rep.displayName}</dd>
                    <dd className="text-sm font-semibold text-[#5f6b66]">{deal.rep.email}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-black uppercase text-[#5f6b66]">Subscription</dt>
                    <dd className="font-black">{tierLabel(deal.organization.tierCode)} - {periodLabel(deal.organization.billingPeriod)}</dd>
                    <dd className="text-sm font-semibold text-[#5f6b66]">{deal.organization.subscriptionStatus || 'Status open'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-black uppercase text-[#5f6b66]">First accrual</dt>
                    <dd className="font-black">{formatLongDate(deal.anchorAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-black uppercase text-[#5f6b66]">Last accrual</dt>
                    <dd className="font-black">{formatLongDate(deal.lastAccrualAt)}</dd>
                  </div>
                </dl>
              </div>

              <div className="panel p-4">
                <h3 className="mb-3 text-lg font-black">Rule</h3>
                <p className="text-xl font-black">{deal.rule?.name || 'Commission rule'}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="status-pill">{formatRule(deal.rule)}</span>
                  <span className="status-pill">{tierLabel(deal.rule?.tier_code)}</span>
                  <span className="status-pill">{periodLabel(deal.rule?.billing_period)}</span>
                </div>
              </div>
            </aside>
          </section>
        </section>
      ) : null}
    </>
  );
}

function PayoutStatementPage({ payoutRoute, onBack, onOpenDeal }) {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isPending = payoutRoute?.type === 'pending';

  const loadStatement = useCallback(async () => {
    if (!payoutRoute) return;
    setLoading(true);
    setError('');
    try {
      const result = isPending
        ? await salesApi.pendingStatement(payoutRoute.repUserId ? { repUserId: payoutRoute.repUserId } : {})
        : await salesApi.payoutStatement(payoutRoute.payoutId);
      setStatement(result.statement || result);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load statement');
    } finally {
      setLoading(false);
    }
  }, [isPending, payoutRoute?.payoutId, payoutRoute?.repUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const payout = statement?.payout || null;
  const items = statement?.items || [];
  const totalCents = Number(statement?.totalCents || 0);
  const allReps = Boolean(statement?.allReps);
  const scopedRepName = isPending && !allReps ? (items[0]?.rep_name || items[0]?.rep_email || null) : null;

  return (
    <>
      <div className="mb-4">
        <button className="ghost-button" type="button" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
          Back
        </button>
      </div>

      <Header
        title={isPending ? 'Pending commission statement' : 'Payout statement'}
        subtitle={
          isPending
            ? allReps
              ? 'All reps - commission items not yet covered by a payout'
              : `${scopedRepName ? `${scopedRepName} - ` : ''}Commission items not yet covered by a payout`
            : payout
              ? `${payout.rep_name || payout.rep_email || 'Rep'} - recorded ${formatDate(payout.paid_at || payout.created_at)}`
              : 'Loading payout'
        }
      />

      {error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{error}</div> : null}

      {loading ? (
        <section className="panel p-5">
          <p className="font-bold text-[#5f6b66]">Loading statement...</p>
        </section>
      ) : (
        <section className="grid gap-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">{isPending ? 'Payable now' : 'Payout total'}</span>
              <strong>{formatMoney(totalCents)}</strong>
            </div>
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Line items</span>
              <strong>{items.length}</strong>
            </div>
            <div className="panel metric">
              <span className="text-sm font-bold text-[#5f6b66]">Status</span>
              <strong className="text-lg">
                <span className={`status-pill ${isPending ? 'warn' : ''}`}>{isPending ? 'pending' : payout?.status || 'paid'}</span>
              </strong>
            </div>
          </div>

          <section className="panel table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {allReps ? <th>Rep</th> : null}
                  <th>Date</th>
                  <th>Deal</th>
                  <th>Event</th>
                  <th>Invoice</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    {allReps ? <td>{item.rep_name || item.rep_email || '-'}</td> : null}
                    <td>{formatDate(item.event_timestamp)}</td>
                    <td>
                      {item.organization_id ? (
                        <button
                          className="ghost-button"
                          type="button"
                          onClick={() => onOpenDeal(item.organization_id, item.rep_user_id || payout?.rep_user_id || null)}
                        >
                          {item.organization_name || 'View deal'}
                        </button>
                      ) : (
                        item.organization_name || 'Manual / rep-level'
                      )}
                    </td>
                    <td><span className="status-pill">{item.event_type}</span></td>
                    <td className="text-sm">{item.stripe_invoice_id || '-'}</td>
                    <td className={Number(item.amount_cents) < 0 ? 'text-red-700 font-bold' : 'font-bold'}>
                      {formatMoney(item.amount_cents)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={allReps ? 5 : 4} className="text-right font-black">Total</td>
                  <td className="font-black">{formatMoney(totalCents)}</td>
                </tr>
              </tbody>
            </table>
            {items.length === 0 ? (
              <p className="p-4 text-sm font-semibold text-[#5f6b66]">
                {isPending ? 'Nothing payable right now.' : 'This payout has no recorded line items.'}
              </p>
            ) : null}
          </section>
        </section>
      )}
    </>
  );
}

function Commissions({ commissions, payouts, repCommissions, isAdmin, onOpenDeal, onOpenPending, onOpenPayout }) {
  const deals = useMemo(() => groupCommissionDeals(commissions), [commissions]);
  const pendingCents = commissions
    .filter((item) => item.status === 'pending')
    .reduce((sum, item) => sum + Number(item.amount_cents || 0), 0);
  // Ledger rows are append-only and never flip to 'paid' — the money that has
  // actually gone out lives in sales_payouts.
  const paidPayouts = payouts.filter((payout) => payout.status === 'paid');
  const paidCents = paidPayouts.reduce((sum, payout) => sum + Number(payout.amount_cents || 0), 0);
  const projectedCents = repCommissions.reduce((sum, item) => sum + Number(item.projected_potential_cents || 0), 0);

  return (
    <>
      <Header title="Commissions" subtitle={`${deals.length} commission ${deals.length === 1 ? 'record' : 'records'}`} />
      <section className="grid gap-4 lg:grid-cols-4">
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Pending</span>
          <strong>{formatMoney(pendingCents)}</strong>
          <button className="ghost-button mt-2" type="button" onClick={() => onOpenPending()}>
            View statement
          </button>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Paid</span>
          <strong>{formatMoney(paidCents)}</strong>
          {paidPayouts.length ? (
            <button className="ghost-button mt-2" type="button" onClick={() => onOpenPayout(paidPayouts[0].id)}>
              Latest statement
            </button>
          ) : null}
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Payouts</span>
          <strong>{payouts.length}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">{isAdmin ? 'Projected' : 'Ledger events'}</span>
          <strong>{isAdmin ? formatMoney(projectedCents) : commissions.length}</strong>
        </div>
      </section>

      {payouts.length ? (
        <section className="panel table-wrap mt-4">
          <table className="data-table">
            <thead>
              <tr>
                {isAdmin ? <th>Rep</th> : null}
                <th>Payout</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id}>
                  {isAdmin ? <td>{payout.rep_name || payout.rep_email}</td> : null}
                  <td>{formatDate(payout.paid_at || payout.created_at)}</td>
                  <td className="font-bold">{formatMoney(payout.amount_cents)}</td>
                  <td><span className="status-pill">{payout.status}</span></td>
                  <td>
                    <button className="ghost-button" type="button" onClick={() => onOpenPayout(payout.id)}>
                      Statement
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {isAdmin && repCommissions.length ? (
        <section className="panel table-wrap mt-4">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rep</th>
                <th>Pending</th>
                <th>Paid</th>
                <th>Deals</th>
                <th>Projected</th>
              </tr>
            </thead>
            <tbody>
              {repCommissions.map((rep) => (
                <tr key={rep.rep_user_id}>
                  <td>
                    <strong>{rep.display_name || rep.name || rep.email}</strong>
                    <div className="text-sm text-[#5f6b66]">{rep.email}</div>
                  </td>
                  <td className="font-bold">
                    <button className="ghost-button" type="button" onClick={() => onOpenPending(rep.rep_user_id)}>
                      {formatMoney(rep.pending_cents)}
                    </button>
                  </td>
                  <td>{formatMoney(rep.paid_cents)}</td>
                  <td>{rep.deal_count}</td>
                  <td className="font-bold">{formatMoney(rep.projected_potential_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="panel table-wrap mt-4">
        <table className="data-table commission-deals-table">
          <thead>
            <tr>
              <th>Church / email</th>
              <th>Rep</th>
              <th>Pending</th>
              <th>Lifetime</th>
              <th>Progress</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => (
              <tr key={deal.key}>
                <td>
                  <strong>{deal.organizationName}</strong>
                  <div className="text-sm text-[#5f6b66]">
                    {deal.pendingAttribution || deal.pendingPayment
                      ? deal.tierName
                      : `${deal.tierName} - ${deal.eventCount} ${deal.eventCount === 1 ? 'event' : 'events'}`}
                  </div>
                </td>
                <td>{deal.repName}</td>
                <td className="font-bold">{formatMoney(deal.pendingCents)}</td>
                <td>{formatMoney(deal.lifetimeCents)}</td>
                <td>{dealProgressLabel(deal)}</td>
                <td>
                  <span className={`status-pill ${deal.pendingAttribution || deal.pendingPayment ? 'warn' : ''}`}>{statusLabel(deal.latestStatus)}</span>
                  <div className="text-xs font-bold text-[#5f6b66]">
                    {deal.latestStatus === 'paid' && deal.paidAt ? formatDate(deal.paidAt) : formatDate(deal.latestAt)}
                  </div>
                </td>
                <td>
                  {deal.organizationId && !deal.pendingPayment ? (
                    <button className="ghost-button" type="button" onClick={() => onOpenDeal(deal.organizationId, deal.repUserId)}>
                      Open
                    </button>
                  ) : deal.pendingAttribution ? (
                    <span className="text-sm font-semibold text-[#5f6b66]">Pending org</span>
                  ) : deal.pendingPayment ? (
                    <span className="text-sm font-semibold text-[#5f6b66]">Waiting payment</span>
                  ) : (
                    <span className="text-sm font-semibold text-[#5f6b66]">Manual</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {deals.length === 0 ? <p className="p-4 text-sm font-semibold text-[#5f6b66]">No commission activity yet.</p> : null}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ask Flock — sales knowledge chatbot
// ---------------------------------------------------------------------------

function renderChatInline(text, keyPrefix) {
  const parts = [];
  const regex = /(\*\*[^*]+?\*\*|`[^`]+?`)/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-b${i}`}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={`${keyPrefix}-c${i}`} className="rounded bg-[#f4efe3] px-1 py-0.5 text-[13px]">
          {token.slice(1, -1)}
        </code>
      );
    }
    lastIndex = match.index + token.length;
    i += 1;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Lightweight hand-rolled markdown (headings, lists, code fences, bold, code
// spans) — the portal deliberately carries no markdown dependency.
function ChatMarkdown({ text }) {
  const blocks = [];
  let list = null;
  let code = null;

  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  (text || '').split('\n').forEach((line) => {
    if (code) {
      if (line.trim().startsWith('```')) {
        blocks.push({ type: 'code', text: code.join('\n') });
        code = null;
      } else {
        code.push(line);
      }
      return;
    }
    const trimmed = line.trim();
    if (trimmed.startsWith('```')) {
      flushList();
      code = [];
      return;
    }
    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushList();
      blocks.push({ type: 'h', depth: heading[1].length, text: heading[2] });
      return;
    }
    const bullet = trimmed.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      if (!list || list.type !== 'ul') {
        flushList();
        list = { type: 'ul', items: [] };
      }
      list.items.push(bullet[1]);
      return;
    }
    const numbered = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (numbered) {
      if (!list || list.type !== 'ol') {
        flushList();
        list = { type: 'ol', items: [] };
      }
      list.items.push(numbered[1]);
      return;
    }
    flushList();
    if (trimmed) {
      blocks.push({ type: 'p', text: trimmed });
    }
  });
  if (code) blocks.push({ type: 'code', text: code.join('\n') });
  flushList();

  return (
    <div className="space-y-2 text-sm leading-6">
      {blocks.map((block, index) => {
        if (block.type === 'h') {
          return (
            <p key={index} className={`font-black ${block.depth <= 2 ? 'text-base' : 'text-sm'}`}>
              {renderChatInline(block.text, index)}
            </p>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const ListTag = block.type;
          return (
            <ListTag key={index} className={`${block.type === 'ul' ? 'list-disc' : 'list-decimal'} space-y-1 pl-5`}>
              {block.items.map((item, i) => (
                <li key={i}>{renderChatInline(item, `${index}-${i}`)}</li>
              ))}
            </ListTag>
          );
        }
        if (block.type === 'code') {
          return (
            <pre key={index} className="overflow-x-auto rounded-lg bg-[#172026] p-3 text-xs text-[#fffaf0]">
              {block.text}
            </pre>
          );
        }
        return <p key={index}>{renderChatInline(block.text, index)}</p>;
      })}
    </div>
  );
}

// Conversation title from the first message: cut at a word boundary (~60
// chars) with an ellipsis instead of a mid-word chop.
function chatTitleFromText(text) {
  const clean = text.trim();
  if (clean.length <= 60) return clean;
  const cut = clean.slice(0, 60);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 30 ? lastSpace : 60).trimEnd()}…`;
}

const ASK_FLOCK_SUGGESTIONS = [
  'How do I take photo attendance?',
  'What does Starter cost vs Pro?',
  'How does a church get onboarded?',
];

function AskFlock({ isAdmin }) {
  const [conversations, setConversations] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  // null = no active content search; a Set of ids once the server responds.
  const [contentMatchIds, setContentMatchIds] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [banner, setBanner] = useState('');
  const [teamView, setTeamView] = useState(false);
  const [overview, setOverview] = useState(null);
  const threadRef = useRef(null);
  // Conversation id owned by an in-flight send — the messages effect must not
  // clobber its optimistic thread (user bubble + streaming assistant bubble).
  const activeSendRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const result = await salesApi.chatConversations();
      setConversations(result.conversations || []);
      setConfigured(result.configured !== false);
    } catch (err) {
      setBanner(err.body?.message || err.message || 'Could not load conversations');
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId || String(selectedId).startsWith('local-')) return;
    // While streaming into this conversation, the optimistic thread owns the
    // view; once sending flips false this re-runs and reconciles with the
    // server (real message ids, persisted content).
    if (sending && activeSendRef.current === selectedId) return;
    let cancelled = false;
    setLoadingThread(true);
    salesApi
      .chatMessages(selectedId)
      .then((result) => {
        if (!cancelled) setMessages(result.messages || []);
      })
      .catch((err) => {
        if (!cancelled) setBanner(err.body?.message || err.message || 'Could not load messages');
      })
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, sending]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Debounced server-side content search — matches words inside message
  // bodies, not just titles. Falls back to title-only filtering on error.
  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setContentMatchIds(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      salesApi
        .searchChatConversations(q, teamView)
        .then((result) => {
          if (!cancelled) setContentMatchIds(new Set(result.conversationIds || []));
        })
        .catch(() => {
          if (!cancelled) setContentMatchIds(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, teamView]);

  async function toggleTeamView() {
    const next = !teamView;
    setTeamView(next);
    setSelectedId(null);
    setMessages([]);
    setBanner('');
    if (next && !overview) {
      try {
        setOverview(await salesApi.chatAdminOverview());
      } catch (err) {
        setBanner(err.body?.message || err.message || 'Could not load team questions');
      }
    }
  }

  function startNewConversation() {
    setSelectedId(null);
    setMessages([]);
    setBanner('');
  }

  async function handleSend(preset) {
    const text = (preset ?? draft).trim();
    if (!text || sending || teamView || !configured) return;
    setBanner('');
    setSending(true);

    const assistantLocalId = `local-assistant-${Date.now()}`;
    try {
      let convId = selectedId;
      if (!convId) {
        const created = await salesApi.createChatConversation({ title: chatTitleFromText(text) });
        convId = created.conversation.id;
        activeSendRef.current = convId;
        setSelectedId(convId);
        setConversations((prev) => [created.conversation, ...prev]);
        setMessages([]);
      } else {
        activeSendRef.current = convId;
      }

      setDraft('');
      setMessages((prev) => [
        ...prev,
        { id: `local-user-${Date.now()}`, role: 'user', content: text },
        { id: assistantLocalId, role: 'assistant', content: '', streaming: true },
      ]);

      let streamError = '';
      await salesApi.sendChatMessage(convId, text, {
        onDelta: (delta) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantLocalId ? { ...m, content: m.content + delta } : m))
          );
        },
        onEvent: (event, payload) => {
          if (event === 'error') {
            streamError = payload.message || 'Ask Flock hit a snag — please try again.';
          }
          if (event === 'done') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantLocalId
                  ? { ...m, id: payload.messageId, stop_reason: payload.stopReason, streaming: false }
                  : m
              )
            );
          }
        },
      });

      if (streamError) setBanner(streamError);
      setMessages((prev) => prev.map((m) => (m.id === assistantLocalId ? { ...m, streaming: false } : m)));
      // Bump the active conversation to the top (matches updated_at ordering).
      setConversations((prev) => {
        const active = prev.find((c) => c.id === convId);
        if (!active) return prev;
        return [{ ...active, updated_at: new Date().toISOString() }, ...prev.filter((c) => c.id !== convId)];
      });
    } catch (err) {
      setBanner(err.body?.message || err.message || "Ask Flock couldn't answer that — please try again.");
      // Drop the empty assistant bubble so the thread isn't left hanging.
      setMessages((prev) => prev.filter((m) => !(m.id === assistantLocalId && !m.content)));
    } finally {
      activeSendRef.current = null;
      setSending(false);
    }
  }

  function handleComposerKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }

  const railConversations = teamView ? overview?.conversations || [] : conversations;
  // Title matches filter instantly; the debounced server search unions in
  // conversations whose message contents match.
  const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visibleConversations = searchWords.length
    ? railConversations.filter((c) => {
      const haystack = `${c.title || ''} ${c.rep_display_name || ''}`.toLowerCase();
      return (
        searchWords.every((word) => haystack.includes(word)) ||
        (contentMatchIds !== null && contentMatchIds.has(c.id))
      );
    })
    : railConversations;
  const spend = overview?.spend;
  const showThreadOnMobile = selectedId !== null || (!teamView && messages.length > 0);

  return (
    <>
      <Header title="Ask Flock" subtitle="Answers from the sales knowledge base — pricing, product, onboarding, objections.">
        {isAdmin ? (
          <button className={teamView ? 'secondary-button' : 'ghost-button'} type="button" onClick={toggleTeamView}>
            <UserGroupIcon className="h-5 w-5" />
            Team questions
          </button>
        ) : null}
      </Header>

      {!configured ? (
        <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm font-bold text-amber-900">
          Ask Flock isn&apos;t set up yet — text Brandon.
        </div>
      ) : null}
      {banner ? (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{banner}</div>
      ) : null}
      {teamView && spend ? (
        <div className="mb-4 rounded-lg bg-teal-50 px-4 py-3 text-sm font-bold text-teal-900">
          Month to date: ${spend.estimatedCostUsd.toFixed(2)} of ${spend.monthlyBudgetUsd.toFixed(0)} budget
        </div>
      ) : null}

      <section className="grid gap-4 lg:h-[calc(100vh-160px)] lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className={`panel p-3 lg:flex lg:min-h-0 lg:flex-col ${showThreadOnMobile ? 'hidden' : ''}`}>
          {!teamView ? (
            <button className="secondary-button mb-3 w-full" type="button" onClick={startNewConversation}>
              <PlusIcon className="h-5 w-5" />
              New conversation
            </button>
          ) : (
            <p className="mb-3 px-1 text-xs font-bold uppercase text-[#5f6b66]">All reps&apos; questions (read-only)</p>
          )}
          <input
            type="search"
            className="mb-2 w-full rounded-lg border border-[#ded9cf] bg-white px-3 py-2 text-sm"
            placeholder="Search conversations…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="grid max-h-[60vh] content-start gap-1 overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1">
            {visibleConversations.length === 0 ? (
              <p className="px-1 py-2 text-sm text-[#5f6b66]">
                {searchWords.length ? 'No conversations match.' : 'No conversations yet.'}
              </p>
            ) : (
              visibleConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  className={`rounded-lg px-3 py-2 text-left text-sm ${
                    selectedId === conversation.id ? 'bg-[#f4b942] font-bold text-[#172026]' : 'hover:bg-[#fdf9ef]'
                  }`}
                  onClick={() => {
                    setBanner('');
                    setSelectedId(conversation.id);
                  }}
                >
                  <span className="line-clamp-2 block">{conversation.title || 'New conversation'}</span>
                  <span className="block text-xs text-[#5f6b66]">
                    {teamView ? `${conversation.rep_display_name || 'Rep'} · ` : ''}
                    {formatDate(conversation.updated_at)}
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        <div className={`panel flex min-h-[420px] flex-col lg:min-h-0 ${!showThreadOnMobile ? 'hidden lg:flex' : ''}`}>
          <div className="flex items-center gap-2 border-b border-[#ded9cf] px-4 py-3 lg:hidden">
            <button className="ghost-button !min-h-[36px] !px-2" type="button" onClick={startNewConversation}>
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <p className="truncate font-bold">
              {railConversations.find((c) => c.id === selectedId)?.title || 'New conversation'}
            </p>
          </div>

          <div ref={threadRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {loadingThread ? <p className="text-sm text-[#5f6b66]">Loading…</p> : null}
            {!loadingThread && messages.length === 0 ? (
              <div className="py-6 text-center">
                <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-[#ded9cf]" />
                <p className="mt-2 font-bold">Ask anything about selling Flock.</p>
                <p className="mt-1 text-sm text-[#5f6b66]">Answers come straight from the sales playbook.</p>
                {!teamView && configured ? (
                  <div className="mx-auto mt-4 flex max-w-md flex-wrap justify-center gap-2">
                    {ASK_FLOCK_SUGGESTIONS.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        className="ghost-button !min-h-[36px] !px-3 text-sm"
                        onClick={() => handleSend(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user' ? 'bg-[#12332d] text-[#fffaf0]' : 'panel-muted'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    message.streaming && !message.content ? (
                      <span className="flex items-center gap-1.5 py-1" aria-label="Ask Flock is thinking">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#5f6b66]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#5f6b66]" style={{ animationDelay: '150ms' }} />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#5f6b66]" style={{ animationDelay: '300ms' }} />
                      </span>
                    ) : (
                      <>
                        <ChatMarkdown text={message.content} />
                        {message.streaming ? <span className="mt-1 inline-block animate-pulse font-black">▍</span> : null}
                        {message.stop_reason === 'client_disconnect' ? (
                          <p className="mt-2 text-xs italic text-[#5f6b66]">Answer was cut short.</p>
                        ) : null}
                        {message.stop_reason === 'length' ? (
                          <p className="mt-2 text-xs italic text-[#5f6b66]">
                            Answer hit the length limit — ask a follow-up to continue.
                          </p>
                        ) : null}
                      </>
                    )
                  ) : (
                    <p className="whitespace-pre-wrap text-sm leading-6">{message.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!teamView ? (
            <div className="border-t border-[#ded9cf] p-3">
              <div className="flex items-end gap-2">
                <textarea
                  className="min-h-[44px] flex-1 resize-none rounded-lg border border-[#ded9cf] bg-white px-3 py-2.5 text-sm"
                  rows={2}
                  maxLength={4000}
                  placeholder={configured ? 'Ask about pricing, features, onboarding…' : 'Ask Flock is unavailable'}
                  value={draft}
                  disabled={!configured || sending}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                />
                <button
                  className="primary-button"
                  type="button"
                  disabled={!configured || sending || !draft.trim()}
                  onClick={() => handleSend()}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                  {sending ? 'Thinking…' : 'Send'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

function Impact({ impact }) {
  return (
    <>
      <Header title="Impact" subtitle="Sales activity tied to church outcomes" />
      <section className="grid gap-4 lg:grid-cols-4">
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Converted</span>
          <strong>{impact?.converted_leads || 0}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Touched</span>
          <strong>{impact?.touched_leads || 0}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Seekers</span>
          <strong>{impact?.represented_seekers || 0}</strong>
        </div>
        <div className="panel metric">
          <span className="text-sm font-bold text-[#5f6b66]">Lifetime</span>
          <strong>{formatMoney(impact?.lifetimeCommissionCents || 0)}</strong>
        </div>
      </section>
    </>
  );
}

const adminSections = [
  { id: 'rules', label: 'Commission Rules' },
  { id: 'payouts', label: 'Payouts' },
  { id: 'conflicts', label: 'Conflicts' },
  { id: 'reps', label: 'Representatives' },
  { id: 'lead-import', label: 'Lead Import' },
];

function defaultRuleForm() {
  return {
    name: 'Standard commission',
    commissionType: 'percentage',
    percentageBps: 2000,
    fixedAmountCents: '',
    tierCode: '',
    billingPeriod: '',
    commissionDurationMonths: 12,
    payoutThresholdCents: 0,
    refundWindowDays: 30,
  };
}

function ruleFormFromRule(item) {
  return {
    name: item.name || 'Standard commission',
    commissionType: Number(item.fixed_amount_cents || 0) > 0 ? 'flat' : 'percentage',
    percentageBps: item.percentage_bps || '',
    fixedAmountCents: item.fixed_amount_cents || '',
    tierCode: item.tier_code || '',
    billingPeriod: item.billing_period || '',
    commissionDurationMonths: item.commission_duration_months == null ? '' : item.commission_duration_months,
    payoutThresholdCents: item.payout_threshold_cents || 0,
    refundWindowDays: item.refund_window_days || 30,
  };
}

function LeadImportAdmin({ onSeedLeads }) {
  const [limit, setLimit] = useState(100);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);

  async function submitImport(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setLastResult(null);
    try {
      const result = await onSeedLeads({ limit: Number(limit) || 25 });
      setLastResult(result);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not import leads');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel p-4">
      <h2 className="text-lg font-black">Import Church Demand leads</h2>
      <p className="mt-1 text-sm text-neutral-600">
        Pulls the top unserved-church clusters (by signal volume) into the lead pool as unassigned
        leads. This runs automatically every night after the demand recluster — use this button to
        pull new demand in immediately. Safe to re-run: existing leads are refreshed with the
        latest signal counts, never duplicated.
      </p>
      <form className="mt-4 flex items-end gap-3" onSubmit={submitImport}>
        <label className="text-sm font-semibold">
          Max clusters
          <input
            className="field mt-1"
            type="number"
            min="1"
            max="500"
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
          />
        </label>
        <button className="primary-button" type="submit" disabled={submitting}>
          {submitting ? 'Importing...' : 'Import leads'}
        </button>
      </form>
      {lastResult ? (
        <p className="mt-3 text-sm font-bold text-emerald-700">
          Done — {Number(lastResult.sourceRowsCreated || 0)} demand-signal source rows imported or
          refreshed. Check the Leads tab.
        </p>
      ) : null}
      {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
    </section>
  );
}

function Admin({ data, section, setSection, onRule, onUpdateRule, onArchiveRule, onApprovePayout, onSeedLeads, onCreatePayout, onOpenPayout, onOpenPending }) {

  return (
    <>
      <Header title="Admin" subtitle="Sales-domain controls" />
      <nav className="admin-subnav" aria-label="Admin sections">
        {adminSections.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-subnav-button ${section === item.id ? 'active' : ''}`}
            onClick={() => setSection(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {section === 'rules' ? (
        <CommissionRulesAdmin
          reps={data.reps}
          rules={data.rules}
          onRule={onRule}
          onUpdateRule={onUpdateRule}
          onArchiveRule={onArchiveRule}
        />
      ) : null}
      {section === 'payouts' ? (
        <PayoutsAdmin
          payouts={data.payouts}
          repCommissions={data.repCommissions}
          onApprovePayout={onApprovePayout}
          onCreatePayout={onCreatePayout}
          onOpenPayout={onOpenPayout}
          onOpenPending={onOpenPending}
        />
      ) : null}
      {section === 'conflicts' ? <ConflictsAdmin conflicts={data.conflicts} /> : null}
      {section === 'reps' ? <RepresentativesAdmin reps={data.reps} /> : null}
      {section === 'lead-import' ? <LeadImportAdmin onSeedLeads={onSeedLeads} /> : null}
    </>
  );
}

function CommissionRulesAdmin({ reps, rules, onRule, onUpdateRule, onArchiveRule }) {
  const [selectedRepId, setSelectedRepId] = useState(() => reps[0]?.user_id || '');
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [rule, setRule] = useState(defaultRuleForm);

  useEffect(() => {
    if (!reps.length) {
      setSelectedRepId('');
      return;
    }
    if (!reps.some((rep) => rep.user_id === selectedRepId)) {
      setSelectedRepId(reps[0].user_id);
    }
  }, [reps, selectedRepId]);

  const selectedRep = useMemo(
    () => reps.find((rep) => rep.user_id === selectedRepId),
    [reps, selectedRepId]
  );
  const selectedRules = useMemo(
    () => rules
      .filter((item) => item.rep_user_id === selectedRepId && item.status !== 'archived')
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0)),
    [rules, selectedRepId]
  );
  const selectedRule = selectedRules.find((item) => item.id === selectedRuleId);

  useEffect(() => {
    if (selectedRuleId && !selectedRules.some((item) => item.id === selectedRuleId)) {
      setSelectedRuleId('');
    }
  }, [selectedRuleId, selectedRules]);

  async function submitRule(event) {
    event.preventDefault();
    if (!selectedRepId) return;
    const payload = {
      repUserId: selectedRepId,
      name: rule.name,
      tierCode: rule.tierCode || null,
      billingPeriod: rule.billingPeriod || null,
      percentageBps: rule.commissionType === 'percentage' ? rule.percentageBps : null,
      fixedAmountCents: rule.commissionType === 'flat' ? rule.fixedAmountCents : null,
      commissionDurationMonths: rule.commissionDurationMonths,
      payoutThresholdCents: rule.payoutThresholdCents,
      refundWindowDays: rule.refundWindowDays,
    };
    if (selectedRule) {
      await onUpdateRule(selectedRule.id, payload);
      setSelectedRuleId('');
    } else {
      await onRule(payload);
    }
    setRule(defaultRuleForm());
    setShowForm(false);
  }

  async function removeSelectedRule() {
    if (!selectedRule) return;
    if (!window.confirm(`Remove ${selectedRule.name}?`)) return;
    await onArchiveRule(selectedRule.id);
    setSelectedRuleId('');
  }

  return (
    <section className="admin-rules-layout">
      <aside className="panel p-4">
        <div className="admin-panel-heading">
          <h2>Representatives</h2>
          <span>{reps.length}</span>
        </div>
        <div className="admin-rep-list">
          {reps.map((rep) => {
            const isSelected = selectedRepId === rep.user_id;
            const repRules = rules.filter((item) => item.rep_user_id === rep.user_id && item.status !== 'archived').length;
            return (
              <button
                key={rep.user_id}
                type="button"
                className={`admin-rep-button ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  setSelectedRepId(rep.user_id);
                  setSelectedRuleId('');
                  setRule(defaultRuleForm());
                  setShowForm(false);
                }}
              >
                <span>
                  <strong>{rep.display_name || rep.name || rep.email}</strong>
                  <small>{rep.email}</small>
                </span>
                <em>{repRules}</em>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="panel p-4">
        <div className="admin-panel-heading">
          <div>
            <h2>{selectedRep ? selectedRep.display_name || selectedRep.name || selectedRep.email : 'Commission rules'}</h2>
            <p>{selectedRules.length} active</p>
          </div>
          <div className="admin-rule-actions">
            <button
              className="ghost-button icon-button"
              type="button"
              title="Add commission rule"
              aria-label="Add commission rule"
              disabled={!selectedRepId}
              onClick={() => {
                setSelectedRuleId('');
                setRule(defaultRuleForm());
                setShowForm(true);
              }}
            >
              <PlusIcon className="h-5 w-5" />
            </button>
            <button
              className="danger-button icon-button"
              type="button"
              title="Remove selected commission rule"
              aria-label="Remove selected commission rule"
              disabled={!selectedRule}
              onClick={removeSelectedRule}
            >
              <MinusIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {showForm ? (
          <form className="admin-rule-form" onSubmit={submitRule}>
            <label className="grid gap-1 text-sm font-bold">
              Rule name
              <input className="field" value={rule.name} onChange={(event) => setRule({ ...rule, name: event.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                aria-pressed={rule.commissionType === 'percentage'}
                className={`ghost-button ${rule.commissionType === 'percentage' ? 'status-action-button active' : ''}`}
                type="button"
                onClick={() => setRule({ ...rule, commissionType: 'percentage', fixedAmountCents: '' })}
              >
                Percent
              </button>
              <button
                aria-pressed={rule.commissionType === 'flat'}
                className={`ghost-button ${rule.commissionType === 'flat' ? 'status-action-button active' : ''}`}
                type="button"
                onClick={() => setRule({ ...rule, commissionType: 'flat', percentageBps: '' })}
              >
                Flat
              </button>
            </div>
            {rule.commissionType === 'percentage' ? (
              <label className="grid gap-1 text-sm font-bold">
                Basis points
                <input className="field" min="1" type="number" value={rule.percentageBps} onChange={(event) => setRule({ ...rule, percentageBps: event.target.value })} />
              </label>
            ) : (
              <label className="grid gap-1 text-sm font-bold">
                Flat amount in cents
                <input className="field" min="1" type="number" value={rule.fixedAmountCents} onChange={(event) => setRule({ ...rule, fixedAmountCents: event.target.value })} />
              </label>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1 text-sm font-bold">
                Tier
                <select className="field" value={rule.tierCode} onChange={(event) => setRule({ ...rule, tierCode: event.target.value })}>
                  <option value="">Any tier</option>
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Period
                <select className="field" value={rule.billingPeriod} onChange={(event) => setRule({ ...rule, billingPeriod: event.target.value })}>
                  <option value="">Any period</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="grid gap-1 text-sm font-bold">
                Months
                <input className="field" min="1" type="number" value={rule.commissionDurationMonths} onChange={(event) => setRule({ ...rule, commissionDurationMonths: event.target.value })} />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Threshold
                <input className="field" min="0" type="number" value={rule.payoutThresholdCents} onChange={(event) => setRule({ ...rule, payoutThresholdCents: event.target.value })} />
              </label>
              <label className="grid gap-1 text-sm font-bold">
                Refund days
                <input className="field" min="0" type="number" value={rule.refundWindowDays} onChange={(event) => setRule({ ...rule, refundWindowDays: event.target.value })} />
              </label>
            </div>
            <button className="primary-button" type="submit">{selectedRule ? 'Save rule' : 'Create rule'}</button>
          </form>
        ) : null}

        <div className="admin-rule-list">
          {selectedRules.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`admin-rule-card ${selectedRuleId === item.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedRuleId(item.id);
                setRule(ruleFormFromRule(item));
                setShowForm(true);
              }}
            >
              <span>
                <strong>{item.name}</strong>
                <small>v{item.version} - {tierLabel(item.tier_code)} - {periodLabel(item.billing_period)}</small>
              </span>
              <span>
                <strong>{formatRule(item)}</strong>
                <small>{item.commission_duration_months == null ? 'Ongoing' : `${item.commission_duration_months} months`}</small>
              </span>
            </button>
          ))}
          {selectedRules.length === 0 ? <p className="p-4 text-sm font-semibold text-[#5f6b66]">No active commission rules.</p> : null}
        </div>
      </section>
    </section>
  );
}

function RepresentativesAdmin({ reps }) {
  return (
    <section className="panel table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Rep</th>
            <th>Role</th>
            <th>Referral</th>
          </tr>
        </thead>
        <tbody>
          {reps.map((rep) => (
            <tr key={rep.user_id}>
              <td>{rep.display_name || rep.name || rep.email}</td>
              <td><span className="status-pill">{rep.role}</span></td>
              <td>{rep.referral_slug}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {reps.length === 0 ? <p className="p-4 text-sm font-semibold text-[#5f6b66]">No representatives yet.</p> : null}
    </section>
  );
}

function PayoutsAdmin({ payouts, repCommissions, onApprovePayout, onCreatePayout, onOpenPayout, onOpenPending }) {
  const [busyRepId, setBusyRepId] = useState('');
  const [busyPayoutId, setBusyPayoutId] = useState('');
  const [error, setError] = useState('');
  const payableReps = repCommissions.filter((rep) => Number(rep.pending_cents || 0) > 0);

  // Same guards as recordPayout: these buttons change the statement of record
  // for real money, so they confirm, disable while in flight, and surface
  // failures — a rejected request must never look like success.
  async function approvePayout(payout, markPaid) {
    const repLabel = payout.rep_name || payout.rep_email;
    const confirmed = window.confirm(
      markPaid
        ? `Mark this ${formatMoney(payout.amount_cents)} payout for ${repLabel} as PAID?\n\nDo this AFTER the money has actually been sent — paid payouts are the immutable statement of record.`
        : `Approve this ${formatMoney(payout.amount_cents)} payout for ${repLabel}?`
    );
    if (!confirmed) return;
    setBusyPayoutId(payout.id);
    setError('');
    try {
      await onApprovePayout(payout.id, markPaid);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not update payout');
    } finally {
      setBusyPayoutId('');
    }
  }

  async function recordPayout(rep) {
    const repLabel = rep.display_name || rep.name || rep.email;
    const confirmed = window.confirm(
      `Record a PAID payout of ${formatMoney(rep.pending_cents)} for ${repLabel}?\n\nDo this AFTER the Zelle transfer is sent — this snapshots the covered commission items as the statement of record.`
    );
    if (!confirmed) return;
    setBusyRepId(rep.rep_user_id);
    setError('');
    try {
      await onCreatePayout(rep.rep_user_id);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not record payout');
    } finally {
      setBusyRepId('');
    }
  }

  return (
    <div className="grid gap-4">
      <section className="panel p-4">
        <h2 className="text-lg font-black">Record a payout</h2>
        <p className="mt-1 text-sm text-[#5f6b66]">
          Zelle the rep first, then record it here. Recording snapshots the covered commission
          items as an immutable statement and clears the rep&apos;s pending balance.
        </p>
        {payableReps.length ? (
          <div className="mt-3 grid gap-2">
            {payableReps.map((rep) => (
              <div key={rep.rep_user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-black/10 px-3 py-2">
                <div>
                  <strong>{rep.display_name || rep.name || rep.email}</strong>
                  <div className="text-sm text-[#5f6b66]">{rep.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="ghost-button" type="button" onClick={() => onOpenPending(rep.rep_user_id)}>
                    View statement
                  </button>
                  <button
                    className="primary-button"
                    type="button"
                    disabled={busyRepId === rep.rep_user_id}
                    onClick={() => recordPayout(rep)}
                  >
                    {busyRepId === rep.rep_user_id ? 'Recording...' : `Record Zelle payout - ${formatMoney(rep.pending_cents)}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm font-semibold text-[#5f6b66]">Nothing payable right now.</p>
        )}
        {error ? <p className="mt-3 text-sm font-bold text-red-700">{error}</p> : null}
      </section>

      <section className="panel table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Rep</th>
              <th>Recorded</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((payout) => (
              <tr key={payout.id}>
                <td>{payout.rep_name || payout.rep_email}</td>
                <td>{formatDate(payout.paid_at || payout.created_at)}</td>
                <td className="font-bold">{formatMoney(payout.amount_cents)}</td>
                <td><span className="status-pill">{payout.status}</span></td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    <button className="ghost-button" type="button" onClick={() => onOpenPayout(payout.id)}>Statement</button>
                    {payout.status === 'pending' ? (
                      <button
                        className="ghost-button"
                        disabled={busyPayoutId === payout.id}
                        type="button"
                        onClick={() => approvePayout(payout, false)}
                      >
                        {busyPayoutId === payout.id ? 'Working...' : 'Approve'}
                      </button>
                    ) : null}
                    {payout.status !== 'paid' && payout.status !== 'canceled' ? (
                      <button
                        className="ghost-button"
                        disabled={busyPayoutId === payout.id}
                        type="button"
                        onClick={() => approvePayout(payout, true)}
                      >
                        {busyPayoutId === payout.id ? 'Working...' : 'Mark paid'}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payouts.length === 0 ? <p className="p-4 text-sm font-semibold text-[#5f6b66]">No payouts yet.</p> : null}
        {error ? <p className="p-4 text-sm font-bold text-red-700">{error}</p> : null}
      </section>
    </div>
  );
}

function ConflictsAdmin({ conflicts }) {
  return (
    <section className="panel table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Conflict</th>
            <th>Existing</th>
            <th>Attempted</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {conflicts.map((conflict) => (
            <tr key={conflict.id}>
              <td>{conflict.normalized_email}</td>
              <td>{conflict.existing_rep_name || conflict.existing_rep_email}</td>
              <td>{conflict.attempted_rep_name || conflict.attempted_rep_email}</td>
              <td><span className="status-pill warn">{conflict.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {conflicts.length === 0 ? <p className="p-4 text-sm font-semibold text-[#5f6b66]">No attribution conflicts.</p> : null}
    </section>
  );
}

export default function App() {
  const portal = useSalesPortal();
  const [routePath, navigate] = useRoutePath();
  const leadPageId = useMemo(() => leadIdFromPath(routePath), [routePath]);
  const dealRoute = useMemo(() => dealRouteFromPath(routePath), [routePath]);
  const payoutRoute = useMemo(() => payoutRouteFromPath(routePath), [routePath]);
  // Lifted so the Admin page remembers its section (e.g. Payouts) across
  // statement/deal round-trips — the Admin component unmounts on sub-pages.
  const [adminSection, setAdminSection] = useState('rules');

  const activeView = useMemo(() => {
    if (leadPageId) return 'lead-detail';
    if (dealRoute) return 'commission-deal';
    if (payoutRoute) return 'payout-statement';
    if (portal.activeTab === 'admin' && !portal.isAdmin) return 'today';
    return portal.activeTab;
  }, [dealRoute, leadPageId, payoutRoute, portal.activeTab, portal.isAdmin]);

  async function copyText(value) {
    await navigator.clipboard.writeText(value);
  }

  function openLeadPage(leadId) {
    portal.setActiveTab('leads');
    navigate(`/leads/${encodeURIComponent(leadId)}`);
  }

  // NOTE: these deliberately do NOT touch the active tab — the sidebar
  // highlight is derived from the route, and preserving the tab lets Back
  // return to the page the user actually came from (e.g. Admin → Payouts).
  function openDealPage(organizationId, repUserId = null) {
    const params = repUserId ? `?repUserId=${encodeURIComponent(repUserId)}` : '';
    navigate(`/deals/${encodeURIComponent(organizationId)}${params}`);
  }

  function openPayoutStatement(payoutId) {
    navigate(`/payouts/${encodeURIComponent(payoutId)}`);
  }

  function openPendingStatement(repUserId = null) {
    const params = repUserId ? `?repUserId=${encodeURIComponent(repUserId)}` : '';
    navigate(`/payouts/pending${params}`);
  }

  function goBack(fallback) {
    if (window.history.state?.fromApp) {
      window.history.back();
    } else {
      fallback();
    }
  }

  function openTab(tabId) {
    navigate('/');
    portal.setActiveTab(tabId);
  }

  function backToLeads() {
    navigate('/');
    portal.setActiveTab('leads');
  }

  function backToCommissions() {
    navigate('/');
    portal.setActiveTab('commissions');
  }

  async function refreshAfter(action) {
    const result = await action();
    await portal.load();
    return result;
  }

  async function handleLogout() {
    clearToken();
    if (auth) {
      await signOut(auth).catch(() => {});
    }
    portal.setSession(null);
  }

  if (!portal.session) {
    return <Login onLogin={portal.setSession} error={portal.error} setError={portal.setError} />;
  }

  return (
    <div className="app-shell">
      <Sidebar
        activeTab={leadPageId ? 'leads' : dealRoute || payoutRoute ? 'commissions' : portal.activeTab}
        setActiveTab={openTab}
        profile={portal.data.me?.profile}
        onRefresh={portal.load}
        loading={portal.loading}
        onLogout={handleLogout}
      />

      <main className="content">
        {portal.error ? <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{portal.error}</div> : null}

        {activeView === 'today' ? (
          <Today data={portal.data} setActiveTab={portal.setActiveTab} onCopy={copyText} />
        ) : null}

        {activeView === 'leads' ? (
          <Leads
            leads={portal.data.leads}
            workbench={portal.data.workbench}
            onOpenLead={openLeadPage}
            isAdmin={portal.isAdmin}
            reps={portal.data.reps}
            onRefreshLeads={portal.load}
            onManualLead={(payload) => refreshAfter(() => salesApi.createManualLead(payload))}
          />
        ) : null}

        {activeView === 'lead-detail' ? (
          <LeadDetailPage
            leadId={leadPageId}
            isAdmin={portal.isAdmin}
            reps={portal.data.reps}
            onBack={backToLeads}
            onRefreshLeads={portal.load}
          />
        ) : null}

        {activeView === 'commission-deal' ? (
          <DealDetailPage
            dealRoute={dealRoute}
            onBack={() => goBack(backToCommissions)}
          />
        ) : null}

        {activeView === 'payout-statement' ? (
          <PayoutStatementPage
            payoutRoute={payoutRoute}
            onBack={() => goBack(backToCommissions)}
            onOpenDeal={openDealPage}
          />
        ) : null}

        {activeView === 'commissions' ? (
          <Commissions
            commissions={portal.data.commissions}
            payouts={portal.data.payouts}
            repCommissions={portal.data.repCommissions}
            isAdmin={portal.isAdmin}
            onOpenDeal={openDealPage}
            onOpenPending={openPendingStatement}
            onOpenPayout={openPayoutStatement}
          />
        ) : null}
        {activeView === 'ask' ? <AskFlock isAdmin={portal.isAdmin} /> : null}
        {activeView === 'impact' ? <Impact impact={portal.data.impact} /> : null}
        {activeView === 'admin' ? (
          <Admin
            data={portal.data}
            section={adminSection}
            setSection={setAdminSection}
            onRule={(payload) => refreshAfter(() => salesApi.createCommissionRule(payload))}
            onUpdateRule={(ruleId, payload) => refreshAfter(() => salesApi.updateCommissionRule(ruleId, payload))}
            onArchiveRule={(ruleId) => refreshAfter(() => salesApi.archiveCommissionRule(ruleId))}
            onApprovePayout={(payoutId, markPaid = false) => refreshAfter(() => salesApi.approvePayout(payoutId, markPaid))}
            onSeedLeads={(payload) => refreshAfter(() => salesApi.seedChurchDemand(payload))}
            onCreatePayout={(repUserId) => refreshAfter(() => salesApi.createPayout({ repUserId }))}
            onOpenPayout={openPayoutStatement}
            onOpenPending={openPendingStatement}
          />
        ) : null}
      </main>
    </div>
  );
}
